import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CleaningServices, Compare, Crop, DragIndicator, ExpandMore, FileDownload, Visibility, VisibilityOff,
} from '@mui/icons-material';
import { Deck, OrthographicView } from '@deck.gl/core';
import { BitmapLayer, LineLayer, PathLayer, PolygonLayer, SolidPolygonLayer } from '@deck.gl/layers';
import { MultiscaleImageLayer, getChannelStats, getDefaultInitialViewState, loadOmeTiff } from '@hms-dbmi/viv';

import { useProfile, withAuthorization } from '../Session';
import { LutExtension, histogram, lutColor } from '../Viewer/lut';
import { openRemoteTiff } from '../Viewer/remoteTiff';
import { loadTiles } from '../Viewer/tileSource';
import { renderRgbTile } from '../Viewer/saturation';
import { blurLoader } from '../Viewer/blur';
import { detectDust, pointInPolygon, polygonArea, simplifyPath } from '../Viewer/tissue';
import {
  DERIVED_DEFAULTS, DERIVED_ID, DIFF_COLORS, DiffExtension, autoBalance, pairLoader, renderLinearTile,
} from '../Viewer/difference';
import {
  BLOOD_DEFAULTS, BLOOD_FALLBACK, BLOOD_ID, BloodExtension, bloodHeatData, estimateBlood, estimateHeat, glassLevel, heatCanvas,
  heatField,
} from '../Viewer/blood';
import {
  NUCLEI_DEFAULTS, NUCLEI_ID, VHE_DEFAULTS, VHE_ID, VirtualHeExtension, estimateNuclei, estimateVirtualHe, stackLoader,
} from '../Viewer/virtualHe';
import {
  REDOX_DEFAULTS, REDOX_FORMULAS, REDOX_ID, RedoxExtension, isFad, isNadh, redoxAuto, redoxData, redoxStats, resolveRedox,
} from '../Viewer/redox';
import {
  COMPOSITE_COLORS, COMPOSITE_DEFAULTS, COMPOSITE_ID, COMPOSITE_KEYS, CompositeExtension, backgroundLevel, balanceGains,
  blackLevels, compositeChannels, compositeRecipe, compositeStats, compositeTransform, compositeWindows,
} from '../Viewer/composite';
import {
  COLLAGEN_DEFAULTS, COLLAGEN_ID, collagenBlocks, collagenCanvas, collagenData, collagenField, collagenStats,
  collagenVectors, vectorLevel,
} from '../Viewer/collagen';
import {
  ATTENTION_DEFAULTS, ATTENTION_ID, attentionAlpha, attentionCanvas, attentionContours, attentionField, attentionView,
  contourColor as contourLutColor, contourLevels, decodePatches, encodePatches, hesRaster, isSigned, parsePatches,
  placePatches, tissueMask,
} from '../Viewer/attention';
import ChannelCard from './ChannelCard';
import DerivedChannelCard from './DerivedChannelCard';
import BloodChannelCard from './BloodChannelCard';
import VirtualHeCard from './VirtualHeCard';
import RedoxCard from './RedoxCard';
import CompositeCard from './CompositeCard';
import CollagenCard from './CollagenCard';
import AttentionCard from './AttentionCard';
import CropOverlay, { fitView } from './CropOverlay';
import { ColorBar, ScaleBar, tickLabel } from './Legend';
import AddImageDialog from './AddImageDialog';
import ExportDialog from './ExportDialog';
import {
  addAttentionMap, canWriteProject, defaultImageLabel, deleteImage, deleteOverlay, forgetImageInSample, imageUrl, pixelSize,
  prepareImage, renameComputedChannel, renameImage, restartImagePreparation, saveImageDisplay, saveSampleComparison,
  setDemo, setOverlayPlacement, setSamplePixelSize, tileUrlFor, toMicrons, watchDemo, watchImages, watchOverlays,
  watchProject, watchSample,
} from '../../services';
import ExternalPreviewButton from '../ExternalPreview';
import * as ROUTES from '../../constants/routes';
import './styles.css';

const LOGO = 'https://hyprview-landing-images.s3.eu-west-3.amazonaws.com/logo_hyprview_thin.png';
const WATERMARK = 'https://hyprview-landing-images.s3.eu-west-3.amazonaws.com/background-logo.png';
// Couleurs proposées par défaut aux modalités sans réglage enregistré.
const PALETTE = [[0, 255, 102], [255, 0, 255], [0, 200, 255], [255, 200, 0], [255, 80, 80], [160, 120, 255]];

// Gomme : glisser trace une zone à effacer (pas de déplacement de la vue, sauf Maj enfoncée).
const ERASE_CONTROLLER = { dragPan: false, doubleClickZoom: false };
const BLACK = [0, 0, 0];
const COMPUTED = [DERIVED_ID, BLOOD_ID, VHE_ID, NUCLEI_ID, REDOX_ID, COMPOSITE_ID, COLLAGEN_ID, ATTENTION_ID];  // canaux calculés, modalités au même titre que les images
const WHITE = [255, 255, 255];

// Images couleur (HES…) : mélange normal, sous les fluorescences, opacité réglable.
const NORMAL = {
  blend: true,
  blendColorOperation: 'add', blendColorSrcFactor: 'src-alpha', blendColorDstFactor: 'one-minus-src-alpha',
  blendAlphaOperation: 'add', blendAlphaSrcFactor: 'one', blendAlphaDstFactor: 'one-minus-src-alpha',
  depthWriteEnabled: false, depthCompare: 'always',
};

// Mélange additif des couches (comme un composite Fiji).
const ADDITIVE = {
  blend: true,
  blendColorOperation: 'add', blendColorSrcFactor: 'one', blendColorDstFactor: 'one',
  blendAlphaOperation: 'add', blendAlphaSrcFactor: 'one', blendAlphaDstFactor: 'one',
  depthWriteEnabled: false, depthCompare: 'always',
};

// Pixels entrelacés (dernière dimension = 3 ou 4 composantes) : image couleur, même test que Viv.
const isInterleaved = shape => [3, 4].includes(shape[shape.length - 1]);

// Source affichée : tuiles JPEG préparées par le serveur, sinon lecture directe de l'OME-TIFF (lente ; images
// envoyées avant le découpage automatique, ou images de démonstration des émulateurs).
const sourceKey = image => image.tiles?.token || 'tiff';

// Charge l'image et calcule contraste initial + histogramme sur le plus petit niveau.
async function loadImage(image) {
  let data;
  if (image.tiles) {
    data = loadTiles(image.tiles, tileUrlFor(image));
  } else {
    const url = await imageUrl(image);
    const res = await loadOmeTiff(url, { source: await openRemoteTiff(url) });
    ({ data } = Array.isArray(res) ? res[0] : res);
  }
  // Couleur (HES…) : affichée telle quelle, sans contraste ni histogramme.
  if (image.tiles?.rgb || isInterleaved(data[0].shape)) return { key: sourceKey(image), loader: data, rgb: true };
  const raster = await data[data.length - 1].getRaster({ selection: { t: 0, z: 0, c: 0 } });
  const stats = getChannelStats(raster.data);
  return {
    key: sourceKey(image), loader: data, stats, histogram: histogram(raster.data, stats.domain),
  };
}

// Les tuiles sont en 8 bits : valeur d'origine (16 bits en général) = bas + v × (haut − bas) / 255. Copie 8 bits
// envoyée telle quelle (fenêtre des tuiles 0–255, ou fichier lu directement) : fenêtre d'origine renseignée sur
// l'image (display.sourceWindow, valeurs du .window.json de la copie), sinon inconnue.
const toOriginal = (image, sourceWindow) => {
  const w = image.tiles?.window?.[0];
  const [low, high] = w && !(w.low === 0 && w.high === 255) ? [w.low, w.high] : sourceWindow || [];
  return high > low ? v => low + (v * (high - low)) / 255 : null;
};
// Inverse : valeur d'origine -> valeur lue (tuiles 8 bits), identité sans fenêtre.
const fromOriginal = (ch, v) => {
  if (!ch.toOriginal) return v;
  const o0 = ch.toOriginal(0);
  return ((v - o0) * 255) / ((ch.toOriginal(255) - o0) || 1);
};

// Contraste enregistré avant le découpage (valeurs d'origine) : converti dans l'échelle 8 bits des tuiles.
function savedContrast(image) {
  const d = image.display || {};
  const w = image.tiles?.window?.[0];
  if (!d.contrast || !w || d.contrastUnit === 'tiles') return d.contrast;
  return d.contrast.map(v => Math.min(255, Math.max(0, ((v - w.low) * 255) / (w.high - w.low))));
}

// Viewer multimodal (modèle v2). Une seule modalité à la fois (fluorescence ou canal calculé) est affichée, seule par
// défaut ; « Comparer au HE » la compare au HE par un rideau (HE à gauche, modalité à droite). Sans modalité active : le
// HE.
function ViewerPage() {
  const { projectId, sampleId } = useParams();
  const profile = useProfile();
  const [project, setProject] = useState(null);  // null aussi quand il n'est pas lisible (démo vue par un externe)
  const [sample, setSample] = useState(undefined);
  const [demo, setDemoDoc] = useState(undefined);  // fiche demos/{sampleId} ; null : pas une démo
  const [images, setImages] = useState(null);
  const [loaded, setLoaded] = useState({});      // imageId -> { loader, stats, histogram } | { error }
  const [settings, setSettings] = useState({});  // imageId -> { color, lut, inverted, visible, contrast }
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);  // téléchargement en pleine résolution
  const exportRef = useRef({ layers: [], background: [0, 0, 0] });  // couches de la vue affichée, pour l'export
  // Canal calculé à partir de deux fluorescences (rapport ou différence), modalité au même titre que les autres.
  const [compare, setCompare] = useState({ a: null, b: null, maskPreview: false, ...DERIVED_DEFAULTS });
  // Canal « sang » : absorbance de l'hémoglobine, à partir d'un canal absorbant (s, 415 nm) et d'une référence (r).
  const [blood, setBlood] = useState({ s: null, r: null, ...BLOOD_DEFAULTS });
  // HE virtuel : noyaux (n, absorbance à 265 nm), référence de diffusion (r), éosine (e, fluorescence à 280 nm).
  const [vhe, setVhe] = useState({ ...VHE_DEFAULTS });
  const [nuclei, setNuclei] = useState({ ...NUCLEI_DEFAULTS });  // canal « Noyaux » (hématoxyline seule)
  const [redox, setRedox] = useState({ ...REDOX_DEFAULTS });  // rapport redox optique
  const [composite, setComposite] = useState({ ...COMPOSITE_DEFAULTS });  // composite RGB
  // Orientation du collagène (SHG) : canal (c), affichage, niveau et échelle d'analyse, seuil, opacité, LUT.
  const [collagen, setCollagen] = useState({ ...COLLAGEN_DEFAULTS });
  // HES + carte d'attention : cartes de l'échantillon (overlays), réglages d'affichage, importation en cours.
  const [overlays, setOverlays] = useState([]);
  const [attention, setAttention] = useState({ ...ATTENTION_DEFAULTS });
  const [attBusy, setAttBusy] = useState(null);
  const [glass, setGlass] = useState({});  // imageId -> niveau du verre (unités lues), estimé sur l'image
  // Modalité affichée en face du HE : id d'image, DERIVED_ID (canal calculé) ou null ; undefined = pas encore choisie.
  const [active, setActive] = useState(undefined);
  const [curtainPos, setCurtainPos] = useState(0.5);
  const [compareHe, setCompareHe] = useState(false);  // rideau de comparaison avec le HE (désactivé par défaut)
  // Ordre des modalités dans la barre latérale (glisser-déposer) : ids d'images et DERIVED_ID. Les modalités absentes
  // de l'ordre enregistré (nouvelles images) viennent à la fin, dans l'ordre par défaut.
  const [order, setOrder] = useState([]);
  const [dragId, setDragId] = useState(null);
  // Canaux de calcul : cartes rangées dans une section repliable sous la liste (canaux qui ne servent qu'à calculer
  // d'autres canaux) ; dropSection : section survolée pendant un glisser-déposer.
  const [stash, setStash] = useState([]);
  const [stashOpen, setStashOpen] = useState(false);
  const [dropSection, setDropSection] = useState(null);
  const [dropAt, setDropAt] = useState(null);  // { id, after } : carte survolée, insertion avant ou après
  // Recadrage : rectangle gardé { x0, y0, x1, y1 } en px pleine résolution (null : image entière) ; le reste est
  // caché (artefacts de bord). cropEdit : poignées affichées.
  const [crop, setCrop] = useState(null);
  const [cropEdit, setCropEdit] = useState(false);
  // Gomme : zones effacées (poussières, objets autour de l'échantillon) { p: [[x, y]…], auto, t, px }, tracées à la main
  // ou détectées (auto) ; chacune ne cache que la modalité t (id d'image ou DERIVED_ID) active quand elle a été créée.
  // Points en px pleine résolution (toutes les images sont dans la même grille ; px : marque des zones tracées dans
  // les pixels de l'image). eraseEdit : mode gomme ; stroke : tracé en cours.
  const [erase, setErase] = useState([]);
  const [eraseEdit, setEraseEdit] = useState(false);
  const [stroke, setStroke] = useState(null);
  const [panKey, setPanKey] = useState(false);  // Maj enfoncée : glisser déplace la vue
  const [detecting, setDetecting] = useState(false);
  const [eraseNote, setEraseNote] = useState(null);
  const strokeRef = useRef(null);
  const eraseHandlers = useRef({});
  const viewListeners = useRef(new Set());
  // Cartes de modalité dépliées (réglages visibles) ; toutes repliées à l'ouverture.
  const [expanded, setExpanded] = useState({});
  const compareLoaded = useRef(false);
  const diffExtension = useMemo(() => [new DiffExtension()], []);
  const bloodExtension = useMemo(() => [new BloodExtension({ lut: blood.lut })], [blood.lut]);
  const containerRef = useRef(null);
  // Rideau : seconde vue deck.gl superposée (modalité active), découpée à partir de curtainPos, vues synchronisées.
  const curtainRef = useRef(null);
  const deck2Ref = useRef(null);
  const viewStateRef = useRef(null);
  const deckRef = useRef(null);
  const extensionsRef = useRef({});
  const blurredRef = useRef({});  // imageId -> { base, sigma, loader } : pyramide floutée, refaite si le flou change

  useEffect(() => {
    if (!profile) return undefined;
    const fail = err => setError(err.code === 'permission-denied'
      ? "This sample doesn't exist or isn't shared with you." : err.message);
    const stops = [
      watchProject(projectId, setProject, () => setProject(null)),  // démo : projet illisible pour un externe
      watchDemo(sampleId, d => setDemoDoc(d?.projectId === projectId ? d : null), () => setDemoDoc(null)),
      watchSample(projectId, sampleId, s => {
        setSample(s);
        if (!s) return;
        document.title = `Viewer - ${s.name}`;
        // Réglages enregistrés (« Enregistrer comme réglages par défaut ») : canal calculé, modalité affichée et
        // position du rideau, repris une fois, à l'ouverture.
        if (!compareLoaded.current) {
          compareLoaded.current = true;
          const saved = s.comparison;
          if (saved?.op) {
            const {
              op, a = null, b = null, gain = DERIVED_DEFAULTS.gain, balance = 0, smooth = DERIVED_DEFAULTS.smooth, context = 0,
              mask = 'mean', threshold = 0.05, inverted = false,
            } = saved;
            setCompare(prev => ({ ...prev, op, a, b, gain, balance, smooth, context, mask, threshold, inverted }));
          }
          if (saved?.blood) {
            const { s: bs = null, r: br = null, ...rest } = saved.blood;
            setBlood(prev => ({ ...prev, s: bs, r: br, ...Object.fromEntries(Object.keys(BLOOD_DEFAULTS)
              .filter(key => rest[key] !== undefined).map(key => [key, rest[key]])) }));
          }
          if (saved?.collagen) {
            setCollagen(prev => ({ ...prev, ...Object.fromEntries(Object.keys(COLLAGEN_DEFAULTS)
              .filter(key => saved.collagen[key] !== undefined).map(key => [key, saved.collagen[key]])) }));
          }
          if (saved?.attention) {
            setAttention(prev => ({ ...prev, ...Object.fromEntries(Object.keys(ATTENTION_DEFAULTS)
              .filter(key => saved.attention[key] !== undefined && saved.attention[key] !== null)
              .map(key => [key, saved.attention[key]])) }));
          }
          if (saved?.vhe) {
            setVhe(prev => ({ ...prev, ...Object.fromEntries(Object.keys(VHE_DEFAULTS)
              .filter(key => saved.vhe[key] !== undefined).map(key => [key, saved.vhe[key]])) }));
          }
          if (saved?.nuclei) {
            setNuclei(prev => ({ ...prev, ...Object.fromEntries(Object.keys(NUCLEI_DEFAULTS)
              .filter(key => saved.nuclei[key] !== undefined).map(key => [key, saved.nuclei[key]])) }));
          }
          if (saved?.redox) {
            setRedox(prev => ({ ...prev, ...Object.fromEntries(Object.keys(REDOX_DEFAULTS)
              .filter(key => saved.redox[key] !== undefined).map(key => [key, saved.redox[key]])) }));
          }
          if (saved?.composite) {
            setComposite(prev => ({ ...prev, ...Object.fromEntries(Object.keys(COMPOSITE_DEFAULTS)
              .filter(key => saved.composite[key] !== undefined).map(key => [key, saved.composite[key]])) }));
          }
          if (saved && 'active' in saved) setActive(saved.active);
          if (saved?.curtainPos) setCurtainPos(saved.curtainPos);
          if (typeof saved?.curtain === 'boolean') setCompareHe(saved.curtain);
          if (Array.isArray(saved?.order)) setOrder(saved.order);
          if (Array.isArray(saved?.stash)) setStash(saved.stash);
          if (saved?.crop) setCrop(saved.crop);
          if (Array.isArray(saved?.erase)) {
            setErase(saved.erase.map(z => ({
              p: Array.from({ length: z.p.length / 2 }, (_, i) => [z.p[2 * i], z.p[2 * i + 1]]), auto: !!z.auto, px: !!z.px,
              t: z.t ?? saved.active ?? null,  // zones enregistrées avant le ciblage : modalité alors affichée
              bg: z.bg ?? null,
            })));
          }
        }
      }, fail),
      watchImages(projectId, sampleId, setImages, fail),
      watchOverlays(projectId, sampleId, setOverlays, () => setOverlays([])),
    ];
    return () => stops.forEach(stop => stop());
  }, [projectId, sampleId, profile?.uid]);

  // Aperçu « vue externe » d'un administrateur : l'échantillon s'affiche en présentation ; s'il n'est ni une démo ni dans
  // un projet partagé avec lui, une note signale qu'un externe ne peut pas encore l'ouvrir.
  const previewMember = (project?.memberIds || []).includes(profile?.uid);
  const previewClosed = !!profile?.preview && demo === null && !previewMember;
  const projectShown = profile?.preview && !previewMember ? null : project;  // projet lisible (aperçu : s'il est partagé)
  const visibleImages = useMemo(
    () => (images || []).filter(img => img.status === 'ready' || img.devUrl),
    [images],
  );

  // Chargement des fichiers des nouvelles images ; réglages initiaux = réglages enregistrés ou valeurs par défaut.
  useEffect(() => {
    visibleImages.forEach((img, i) => {
      const key = sourceKey(img);
      if (loaded[img.id]?.key === key) return;  // déjà chargée (ou en cours) depuis la même source
      setLoaded(prev => ({ ...prev, [img.id]: { key, loading: true } }));
      loadImage(img)
        .then(result => {
          setLoaded(prev => ({ ...prev, [img.id]: result }));
          const d = img.display || {};
          setSettings(prev => ({
            ...prev,
            [img.id]: result.rgb ? { opacity: d.opacity ?? 1, saturation: d.saturation ?? 1 } : {
              color: d.color || PALETTE[i % PALETTE.length],
              lut: d.lut || 'color',
              inverted: !!d.inverted,
              opacity: d.opacity ?? 1,
              blur: d.blur ?? 0,
              contrast: savedContrast(img) || result.stats.contrastLimits,
              sourceWindow: d.sourceWindow || null,
              lutRange: d.lutRange || null,  // partie de la LUT utilisée
            },
          }));
        })
        .catch(err => setLoaded(prev => ({ ...prev, [img.id]: { key, error: err.message } })));
    });
  }, [visibleImages]);

  const channels = visibleImages
    .filter(img => loaded[img.id]?.loader && settings[img.id])
    .map(img => ({
      id: img.id,
      name: img.label || img.modality,
      modality: img.modality,
      ...settings[img.id],
      loader: loaded[img.id].loader,
      rgb: !!loaded[img.id].rgb,
      domain: loaded[img.id].stats?.domain,
      histogram: loaded[img.id].histogram,
      tiled: !!img.tiles,
      toOriginal: toOriginal(img, settings[img.id].sourceWindow),
      // Échelle d'origine connue : image 16 bits, ou tuiles / copie 8 bits dont la fenêtre d'origine est connue.
      scaleKnown: loaded[img.id].loader[0].dtype !== 'Uint8' || !!toOriginal(img, settings[img.id].sourceWindow),
    }));

  // Canal calculé : modalités A et B (par défaut les deux premières fluorescences), pyramide à deux canaux.
  const fluo = channels.filter(ch => !ch.rgb);
  const he = channels.filter(ch => ch.rgb);
  const chA = fluo.find(ch => ch.id === compare.a) || fluo[0];
  const chB = fluo.find(ch => ch.id === compare.b && ch.id !== chA?.id) || fluo.find(ch => ch.id !== chA?.id);
  // Pyramide affichée d'une fluorescence : floutée si sa carte le demande (σ en px pleine résolution).
  const loaderFor = ch => {
    if (!ch.blur) return ch.loader;
    const cached = blurredRef.current[ch.id];
    if (cached?.base === ch.loader && cached.sigma === ch.blur) return cached.loader;
    const loader = blurLoader(ch.loader, ch.blur);
    blurredRef.current[ch.id] = { base: ch.loader, sigma: ch.blur, loader };
    return loader;
  };
  // A et B lues floutées chacune selon sa carte (px pleine résolution), plus le lissage du canal calculé (px de chaque
  // niveau, donc à tous les zooms), avant le rapport ou la différence.
  const smooth = compare.smooth ?? DERIVED_DEFAULTS.smooth;
  const pairInput = (ch, sigma = smooth) => (ch.blur || sigma ? blurLoader(ch.loader, ch.blur || 0, sigma) : ch.loader);
  const pair = useMemo(
    () => (chA && chB ? pairLoader(pairInput(chA), pairInput(chB)) : null),
    [chA?.loader, chB?.loader, chA?.blur, chB?.blur, smooth],
  );
  // Réglages du canal calculé, plus l'inversion de A et de B telle que choisie sur leurs cartes.
  const derivedSettings = { ...compare, invertA: !!chA?.inverted, invertB: !!chB?.inverted };

  // Canal sang : absorbant = trans415 (bande de Soret) par défaut, référence = trans530, sinon trans625, sinon une
  // autre transmission. Proposé dès qu'il y a une transmission et une autre modalité.
  const isTrans = ch => /^trans/i.test(ch.modality || '');
  const byModality = (m, not) => fluo.find(ch => ch.modality === m && ch.id !== not);
  const chS = fluo.find(ch => ch.id === blood.s) || byModality('trans415') || fluo.find(isTrans) || fluo[0];
  const chR = fluo.find(ch => ch.id === blood.r && ch.id !== chS?.id) || byModality('trans530', chS?.id)
    || byModality('trans625', chS?.id) || fluo.find(ch => isTrans(ch) && ch.id !== chS?.id) || fluo.find(ch => ch.id !== chS?.id);
  // Canaux lus tels qu'affichés sur leurs cartes (flou de la carte seulement) : pas de lissage propre au canal sang.
  const bloodPair = useMemo(
    () => (chS && chR ? pairLoader(pairInput(chS, 0), pairInput(chR, 0)) : null),
    [chS?.loader, chR?.loader, chS?.blur, chR?.blur],
  );
  // Niveau du verre d'une transmission (canal sang, HE virtuel), estimé une fois par image.
  const requestGlass = ch => {
    if (!ch || ch.id in glass) return;
    setGlass(prev => ({ ...prev, [ch.id]: null }));
    glassLevel(ch.loader).then(v => setGlass(prev => ({ ...prev, [ch.id]: v })))
      .catch(err => console.error('Glass level:', err));
  };
  useEffect(() => { [chS, chR].forEach(requestGlass); }, [chS?.id, chR?.id, chS?.loader, chR?.loader]);
  // Contraste [noir, verre] de chaque canal (unités lues) : Viv en tire directement la transmittance.
  const bloodCal = chS && chR && glass[chS.id] != null && glass[chR.id] != null
    ? { s: [fromOriginal(chS, blood.dark), glass[chS.id]], r: [fromOriginal(chR, blood.dark), glass[chR.id]] } : null;
  const heatmap = blood.display !== 'pixels';
  const heatKeyFor = () => [chS.id, chR.id, ...bloodCal.s, ...bloodCal.r, blood.heatLevel].join('|');
  const estimateBloodNow = async () => {
    if (!bloodCal || bloodPair?.error) return;
    // Carte de chaleur : estimation sur ses mailles, champ lissé tel qu'affiché ; pixel par pixel : sur un niveau
    // d'au moins 4 millions de pixels (estimateBlood).
    let result;
    if (heatmap) {
      const key = heatKeyFor();
      const data = heatData?.key === key ? heatData.data : await bloodHeatData(chS.loader, chR.loader, bloodCal, blood.heatLevel);
      result = estimateHeat(data, { heatSmooth: blood.heatSmooth });
    } else {
      result = await estimateBlood(chS.loader, chR.loader, bloodCal);
    }
    if (result) { setBlood(prev => ({ ...prev, ...result })); setSaveState(null); }
  };

  // HE virtuel : noyaux = trans265 (absorbance des acides nucléiques), référence = trans325 (sinon trans375, sinon une
  // autre transmission), éosine = epi280 (sinon une autre fluorescence). Proposé avec deux transmissions et une
  // fluorescence ; les trois images doivent être dans la même grille de pixels.
  const isEpi = ch => /^epi/i.test(ch.modality || '');
  const chN = fluo.find(ch => ch.id === vhe.n) || byModality('trans265') || fluo.find(isTrans);
  const chNR = fluo.find(ch => ch.id === vhe.r && ch.id !== chN?.id) || byModality('trans325', chN?.id)
    || byModality('trans375', chN?.id) || fluo.find(ch => isTrans(ch) && ch.id !== chN?.id);
  const chE = fluo.find(ch => ch.id === vhe.e) || byModality('epi280') || fluo.find(isEpi);
  // Collagène (safran) : le SHG s'il y en a un, facultatif ('none' : sans safran).
  const chC = vhe.c === 'none' ? null : fluo.find(ch => ch.id === vhe.c) || byModality('shg') || null;
  const vheStack = useMemo(
    () => (chN && chNR && chE
      ? stackLoader([chN, chNR, chE, ...(chC ? [chC] : [])].map(ch => pairInput(ch, 0))) : null),
    [chN?.loader, chNR?.loader, chE?.loader, chC?.loader, chN?.blur, chNR?.blur, chE?.blur, chC?.blur],
  );
  const vheExtension = useMemo(() => [new VirtualHeExtension({ collagen: !!chC })], [!!chC]);
  useEffect(() => { [chN, chNR].forEach(requestGlass); }, [chN?.id, chNR?.id, chN?.loader, chNR?.loader]);
  const vheCal = chN && chNR && glass[chN.id] != null && glass[chNR.id] != null
    ? { n: [fromOriginal(chN, 0), glass[chN.id]], r: [fromOriginal(chNR, 0), glass[chNR.id]] } : null;
  const estimateVheNow = async () => {
    if (!vheCal || vheStack?.error) return;
    const result = await estimateVirtualHe(chN.loader, chNR.loader, chE.loader, vheCal, chC?.loader);
    if (result) { setVhe(prev => ({ ...prev, ...result })); setSaveState(null); }
  };

  // Noyaux seuls : l'hématoxyline du HE virtuel, mêmes canaux par défaut (trans265, référence trans325 ou trans375).
  const chH = fluo.find(ch => ch.id === nuclei.n) || byModality('trans265') || fluo.find(isTrans);
  const chHR = fluo.find(ch => ch.id === nuclei.r && ch.id !== chH?.id) || byModality('trans325', chH?.id)
    || byModality('trans375', chH?.id) || fluo.find(ch => isTrans(ch) && ch.id !== chH?.id);
  const nucStack = useMemo(
    () => (chH && chHR ? stackLoader([chH, chHR].map(ch => pairInput(ch, 0))) : null),
    [chH?.loader, chHR?.loader, chH?.blur, chHR?.blur],
  );
  const nucExtension = useMemo(() => [new VirtualHeExtension({ nuclei: true })], []);
  useEffect(() => { [chH, chHR].forEach(requestGlass); }, [chH?.id, chHR?.id, chH?.loader, chHR?.loader]);
  const nucCal = chH && chHR && glass[chH.id] != null && glass[chHR.id] != null
    ? { n: [fromOriginal(chH, 0), glass[chH.id]], r: [fromOriginal(chHR, 0), glass[chHR.id]] } : null;
  const estimateNucleiNow = async () => {
    if (!nucCal || nucStack?.error) return;
    const result = await estimateNuclei(chH.loader, chHR.loader, nucCal);
    if (result) { setNuclei(prev => ({ ...prev, ...result })); setSaveState(null); }
  };

  // Redox ratio : NAD(P)H = fluorescence excitée dans l'UV proche (epi330 à epi390, epi375), FAD = excitée dans le bleu
  // (epi430 à epi500, epi470) ; images lissées (σ du canal) avant le rapport. Comptes d'origine lus sur un niveau
  // réduit une fois (fenêtre d'origine comprise), puis valeurs automatiques et chiffres recalculés à chaque réglage.
  const chRN = fluo.find(ch => ch.id === redox.n) || fluo.find(isNadh);
  const chRF = fluo.find(ch => ch.id === redox.f && ch.id !== chRN?.id) || fluo.find(ch => isFad(ch) && ch.id !== chRN?.id);
  const redoxStack = useMemo(
    () => (chRN && chRF ? stackLoader([chRN, chRF].map(ch => pairInput(ch, redox.smooth))) : null),
    [chRN?.loader, chRF?.loader, chRN?.blur, chRF?.blur, redox.smooth],
  );
  const redoxExtension = useMemo(() => [new RedoxExtension({ lut: redox.lut })], [redox.lut]);
  const redoxKey = active === REDOX_ID && redoxStack?.loader
    ? [chRN.id, chRF.id, ...(chRN.sourceWindow || []), ...(chRF.sourceWindow || [])].join('|') : null;
  const [redoxRaw, setRedoxRaw] = useState(null);
  useEffect(() => {
    if (!redoxKey || redoxRaw?.key === redoxKey) return undefined;
    let cancelled = false;
    redoxData(chRN.loader, chRF.loader, chRN.toOriginal, chRF.toOriginal)
      .then(data => { if (!cancelled) setRedoxRaw({ key: redoxKey, data }); })
      .catch(err => console.error('Redox ratio:', err));
    return () => { cancelled = true; };
  }, [redoxKey]);
  const redoxReady = redoxRaw && redoxRaw.key === redoxKey ? redoxRaw.data : null;
  const redoxAutoValues = useMemo(
    () => (redoxReady ? redoxAuto(redoxReady, redox) : null),
    [redoxReady, redox.bgN, redox.bgF, redox.threshold, redox.calib, redox.formula],
  );
  const redoxResolved = resolveRedox(redox, redoxAutoValues);
  const redoxStatsValue = useMemo(
    () => (redoxReady && redoxResolved.bgN != null && redoxResolved.bgF != null ? redoxStats(redoxReady, redoxResolved) : null),
    [redoxReady, redoxResolved.bgN, redoxResolved.bgF, redoxResolved.threshold, redox.calib, redox.formula],
  );

  // Composite RGB : trois fluorescences (par défaut les excitations les plus longues en rouge, vert, bleu), lues telles
  // qu'affichées sur leurs cartes (flou compris), lissées (σ en px de chaque niveau) contre le bruit coloré.
  const compChans = compositeChannels(fluo, composite);
  const compositeStack = useMemo(
    () => (compChans.every(Boolean) ? stackLoader(compChans.map(ch => pairInput(ch, composite.smooth ?? 1))) : null),
    [...compChans.map(ch => ch?.loader), ...compChans.map(ch => ch?.blur), composite.smooth],
  );
  const compositeExtension = useMemo(() => [new CompositeExtension()], []);
  // Différences accentuées : statistiques du tissu refaites quand les images, leurs contrastes ou les gains changent.
  // Fond de chaque image (niveau mis à 0 par défaut), estimé une fois par image.
  const [compBg, setCompBg] = useState({});
  useEffect(() => {
    if (active !== COMPOSITE_ID) return;
    compChans.filter(ch => ch && !(ch.id in compBg)).forEach(ch => {
      setCompBg(prev => ({ ...prev, [ch.id]: null }));
      backgroundLevel(ch).then(v => setCompBg(prev => ({ ...prev, [ch.id]: v })))
        .catch(err => console.error('RGB composite, background:', err));
    });
  }, [active, ...compChans.map(ch => ch?.id)]);
  const compBackgrounds = compChans.map(ch => (ch ? compBg[ch.id] ?? null : null));
  const compWindows = compChans.every(Boolean) ? compositeWindows(compChans, composite, compBackgrounds) : [];
  const compBlacks = compChans.every(Boolean) ? blackLevels(compChans, composite, compBackgrounds) : [];
  // Statistiques gardées pendant un changement de seuil (base inchangée), le masque suivant le seuil tout de suite.
  const compStatsBase = active === COMPOSITE_ID && composite.display === 'decorrelated' && compChans.every(Boolean)
    ? [...compChans.map(ch => ch.id), ...compWindows.flat().map(v => v.toFixed(3))].join('|') : null;
  const compStatsKey = compStatsBase && `${compStatsBase}|${composite.threshold ?? 'otsu'}`;
  const [compStats, setCompStats] = useState(null);
  useEffect(() => {
    if (!compStatsKey || compStats?.key === compStatsKey) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => compositeStats(compChans, compWindows, composite.threshold ?? null)
      .then(data => { if (!cancelled) setCompStats({ key: compStatsKey, base: compStatsBase, data }); })
      .catch(err => console.error('RGB composite:', err)), 300);  // réglage en cours : on attend
    return () => { cancelled = true; clearTimeout(timer); };
  }, [compStatsKey]);
  const compStatsReady = compStats && compStats.base === compStatsBase ? compStats.data : null;
  const compTransform = useMemo(
    () => compositeTransform(compStatsReady, composite),
    [compStatsReady, composite.display, composite.strength, composite.contrast, composite.saturation, composite.threshold,
      composite.black],
  );

  // Orientation du collagène : SHG (modalité shg par défaut).
  const chCol = fluo.find(ch => ch.id === collagen.c) || byModality('shg');

  const canWrite = canWriteProject(profile, project);
  // Cartes de la barre latérale : HE, fluorescences et canal calculé, dans l'ordre choisi par l'utilisateur.
  const rank = id => (order.includes(id) ? order.indexOf(id) : order.length);
  const cards = [...he, ...fluo].map(ch => ({ id: ch.id, ch }))
    .concat(fluo.length >= 2 ? [{ id: DERIVED_ID }] : [])
    .concat(fluo.length >= 2 && fluo.some(isTrans) ? [{ id: BLOOD_ID }] : [])
    .concat(fluo.filter(isTrans).length >= 2 && fluo.some(isEpi) ? [{ id: VHE_ID }] : [])
    .concat(fluo.filter(isTrans).length >= 2 ? [{ id: NUCLEI_ID }] : [])
    .concat(fluo.some(isNadh) && fluo.some(isFad) ? [{ id: REDOX_ID }] : [])
    .concat(fluo.length >= 3 ? [{ id: COMPOSITE_ID }] : [])
    .concat(fluo.some(ch => ch.modality === 'shg') ? [{ id: COLLAGEN_ID }] : [])
    .concat(he.length && (overlays.length || canWrite) ? [{ id: ATTENTION_ID }] : [])  // importation : contributeurs
    .sort((x, y) => rank(x.id) - rank(y.id));
  const shownCards = cards.filter(c => !stash.includes(c.id));
  const stashCards = cards.filter(c => stash.includes(c.id));

  // Modalité active : celle enregistrée, sinon la première fluorescence de la liste une fois toutes les images
  // chargées ; si son image est supprimée, aucune (une image encore en chargement n'est pas une image supprimée).
  const allLoaded = images !== null && visibleImages.every(img => loaded[img.id] && !loaded[img.id].loading);
  useEffect(() => {
    if (active === undefined) {
      const firstFluo = cards.find(c => c.ch && !c.ch.rgb);
      if (sample && allLoaded && firstFluo) setActive(firstFluo.id);
    } else if (active && !COMPUTED.includes(active) && images && !images.some(img => img.id === active)) {
      setActive(null);
    }
  }, [active, allLoaded, images, sample]);
  const activeChannel = fluo.find(ch => ch.id === active);
  const derivedShown = active === DERIVED_ID && !!pair?.loader;
  const bloodShown = active === BLOOD_ID && !!bloodPair?.loader && !!bloodCal;
  const vheShown = active === VHE_ID && !!vheStack?.loader && !!vheCal;
  const nucleiShown = active === NUCLEI_ID && !!nucStack?.loader && !!nucCal;
  const redoxShown = !!redoxKey && redoxResolved.bgN != null && redoxResolved.bgF != null;
  const compositeShown = active === COMPOSITE_ID && !!compositeStack?.loader;
  // Orientation du collagène : SHG lu une fois au niveau d'analyse, puis tenseur (échelle σ), carte (affichage, seuil,
  // opacité, LUT) et chiffres recalculés à chaque réglage.
  const collagenShown = active === COLLAGEN_ID && !!chCol;
  const [colData, setColData] = useState(null);
  const colKey = collagenShown ? `${chCol.id}|${collagen.detail}` : null;
  useEffect(() => {
    if (!colKey || colData?.key === colKey) return undefined;
    let cancelled = false;
    collagenData(chCol.loader, collagen.detail)
      .then(data => { if (!cancelled) setColData({ key: colKey, data }); })
      .catch(err => console.error('Collagen orientation:', err));
    return () => { cancelled = true; };
  }, [colKey]);
  const colReady = colData && colData.key === colKey ? colData.data : null;
  const colThreshold = collagen.threshold ?? colReady?.otsu ?? 0.2;
  const colField = useMemo(() => (colReady ? collagenField(colReady, { sigma: collagen.sigma }) : null), [colReady, collagen.sigma]);
  const colImage = useMemo(
    () => (colField && collagen.display !== 'none' ? collagenCanvas(colReady, colField, { ...collagen, threshold: colThreshold }) : null),
    [colField, collagen.display, colThreshold, collagen.opacity, collagen.lut, collagen.lutRange],
  );
  const colStats = useMemo(() => (colField ? collagenStats(colField, colThreshold) : null), [colField, colThreshold]);
  // Vecteurs : tenseur moyenné par blocs (pyramide), niveau de blocs suivant le zoom (vecLevel, mis à jour par la vue),
  // traits du niveau.
  const colBlocks = useMemo(
    () => (colField && collagen.vectors ? collagenBlocks(colReady, colField, colThreshold) : null),
    [colField, colThreshold, collagen.vectors],
  );
  const [vecLevel, setVecLevel] = useState(null);
  const colVectors = useMemo(
    () => (colBlocks && vecLevel != null ? collagenVectors(colBlocks, vecLevel, colReady.cell, collagen) : null),
    [colBlocks, vecLevel, collagen.vecLength],
  );
  // Données binaires des traits (mêmes objets d'un rendu à l'autre : deck.gl ne recharge les tampons qu'au changement).
  const colVectorData = useMemo(() => {
    if (!colVectors?.length) return null;
    const ends = {
      getSourcePosition: { value: colVectors.ends, size: 2, stride: 16, offset: 0 },
      getTargetPosition: { value: colVectors.ends, size: 2, stride: 16, offset: 8 },
    };
    return {
      plain: { length: colVectors.length, attributes: ends },
      colored: { length: colVectors.length, attributes: { ...ends, getColor: { value: colVectors.colors, size: 4, normalized: true } } },
    };
  }, [colVectors]);
  // HES + carte d'attention : patchs de la carte choisie (sinon la première), HES réduit lu une fois (calage, masque du
  // tissu, champ), champ refait si le lissage ou le calage change, image à chaque réglage d'affichage.
  const heCh = he[0];
  const attMap = overlays.find(o => o.id === attention.o) || overlays[0] || null;
  const attPatches = useMemo(
    () => (attMap ? { ...decodePatches(attMap.points), patchSize: attMap.patchSize } : null),
    [attMap?.id, attMap?.points],
  );
  const attentionShown = active === ATTENTION_ID && !!heCh && !!attMap?.transform;
  const [attHes, setAttHes] = useState(null);  // { key (id du HES), data, mask }
  const loadAttHes = async () => {
    if (attHes?.key === heCh.id) return attHes;
    const data = await hesRaster(heCh.loader);
    const next = { key: heCh.id, data, mask: tissueMask(data) };
    setAttHes(next);
    return next;
  };
  useEffect(() => {
    if (attentionShown) loadAttHes().catch(err => console.error('Attention map, HES:', err));
  }, [attentionShown, heCh?.id]);
  const attReady = attHes && attHes.key === heCh?.id ? attHes : null;
  const isolines = attention.display === 'contours';
  // Valeurs signées (contributions) : centrées sur 0, palette divergente ; sinon rangs (attention).
  const attSigned = useMemo(() => (attPatches ? isSigned(attPatches.v) : false), [attPatches]);
  const attValue = attMap?.value || 'attention';  // nom de la colonne des valeurs (légendes)
  // Seuil bas (valeurs signées) : curseur du minimum au maximum des valeurs, nombre de patchs comptés comme 0.
  const attRange = useMemo(
    () => (attPatches ? [Math.min(...attPatches.v), Math.max(...attPatches.v)] : [0, 0]),
    [attPatches],
  );
  const attIgnored = attPatches && attSigned && attention.sBelow != null
    ? Array.from(attPatches.v).filter(v => v < attention.sBelow).length : 0;
  const attShown = attentionView(attention, attSigned);  // réglages du mode affiché (isolignes, valeurs signées : les leurs)
  const attFieldValue = useMemo(
    () => (attReady && attPatches && attMap?.transform
      ? attentionField(attPatches, attMap.transform, attReady.data, attReady.mask, {
        sigma: attShown.sigma, signed: attSigned, emphasis: attention.emphasis,
        minValue: attSigned && attention.sBelow != null ? attention.sBelow : -Infinity,
      })
      : null),
    [attReady, attPatches, attMap?.transform?.join(), attShown.sigma, attSigned, attention.emphasis, attention.sBelow],
  );
  // Réglages affichés et place de 0 dans la palette (valeurs signées : 0,5 sans seuil, plus bas avec un seuil bas).
  const attView = { ...attShown, zero: attFieldValue?.zero ?? 0.5, centered: attFieldValue?.centered ?? true };
  const attImage = useMemo(
    () => (attFieldValue && !isolines ? attentionCanvas(attFieldValue, attShown) : null),
    [attFieldValue, isolines, attShown.threshold, attShown.opacity, attShown.lut, attShown.lutRange?.join(), attSigned],
  );
  // Isolignes : refaites si le champ ou les niveaux changent ; couleurs (LUT) et traits à chaque rendu.
  const attContours = useMemo(
    () => (attFieldValue && isolines ? attentionContours(attFieldValue, contourLevels(attView)) : null),
    [attFieldValue, isolines, attShown.threshold, attShown.step],
  );
  const contourColor = level => [...contourLutColor(level, attView).map(Math.round), Math.round(255 * attention.opacity)];
  // Valeur réelle d'une position t de la palette (valeurs signées : de bas à haut), pour les légendes.
  const attLabel = t => {
    if (!attSigned) return `${Math.round(t * 100)}th`;
    const { low = 0, high = 0 } = attFieldValue || {};
    const v = Math.abs(t - attView.zero) < 1e-9 ? 0 : low + t * (high - low);
    const text = Math.abs(v) < 0.01 && v !== 0 ? Math.abs(v).toExponential(1) : tickLabel(Math.abs(v));
    return v > 0 ? `+${text}` : v < 0 ? `−${text}` : '0';
  };
  // Importation d'un CSV de patchs (contributeurs) : lecture, calage sur le HES (quelques secondes), nouvelle carte
  // affichée aussitôt. Nouveau calage d'une carte : mêmes étapes, patchs déjà enregistrés.
  const placeOnHes = async patches => {
    setAttBusy('Reading the HES…');
    const { data } = await loadAttHes();
    const placed = await placePatches(patches, data, { onProgress: setAttBusy });
    return { transform: placed.transform, placement: { method: placed.method, r2: placed.r2, imageId: heCh.id } };
  };
  const importAttention = async file => {
    try {
      setAttBusy('Reading the patches…');
      const patches = parsePatches(await file.text());
      const { transform, placement } = await placeOnHes(patches);
      const id = await addAttentionMap(profile, projectId, sampleId, {
        name: file.name, value: patches.valueName, points: encodePatches(patches), count: patches.count,
        patchSize: patches.patchSize, transform, placement,
      });
      setAttention(prev => ({ ...prev, o: id }));
      setActive(ATTENTION_ID);
      setSaveState(null);
    } catch (err) {
      window.alert(`Import failed: ${err.message}`);
    } finally {
      setAttBusy(null);
    }
  };
  const refitAttention = async map => {
    try {
      const { transform, placement } = await placeOnHes({ ...decodePatches(map.points), patchSize: map.patchSize });
      await setOverlayPlacement(projectId, sampleId, map.id, transform, placement);
    } catch (err) {
      window.alert(`Refit failed: ${err.message}`);
    } finally {
      setAttBusy(null);
    }
  };
  const removeAttention = map => {
    if (!window.confirm(`Delete the attention map "${map.name}" for all users?`)) return;
    deleteOverlay(projectId, sampleId, map.id).catch(err => window.alert(`Deletion failed: ${err.message}`));
  };
  // k et échelle des noyaux estimés à la première ouverture du HE virtuel (tant qu'ils ne sont pas réglés).
  useEffect(() => {
    if (vheShown && vhe.k == null) estimateVheNow().catch(err => console.error('Virtual HE:', err));
  }, [vheShown, vhe.k == null, !!vheCal]);
  useEffect(() => {
    if (nucleiShown && nuclei.k == null) estimateNucleiNow().catch(err => console.error('Nuclei:', err));
  }, [nucleiShown, nuclei.k == null, !!nucCal]);
  // Carte de chaleur : OD lues une fois au niveau des mailles (refaites si les canaux, l'étalonnage ou le niveau
  // changent), puis image recolorée à chaque réglage (k, seuil, maximum, LUT, lissage, opacité).
  const [heatData, setHeatData] = useState(null);
  const heatKey = bloodShown && heatmap ? heatKeyFor() : null;
  useEffect(() => {
    if (!heatKey || heatData?.key === heatKey) return undefined;
    let cancelled = false;
    bloodHeatData(chS.loader, chR.loader, bloodCal, blood.heatLevel)
      .then(data => { if (!cancelled) setHeatData({ key: heatKey, data }); })
      .catch(err => console.error('Heat map:', err));
    return () => { cancelled = true; };
  }, [heatKey]);
  const heatFieldValue = useMemo(
    () => (heatData && heatData.key === heatKey
      ? heatField(heatData.data, { k: blood.k ?? BLOOD_FALLBACK.k, heatSmooth: blood.heatSmooth }) : null),
    [heatData, heatKey, blood.k, blood.heatSmooth],
  );
  const heatImage = useMemo(
    () => (heatFieldValue ? heatCanvas(heatFieldValue, {
      odMin: blood.odMin ?? BLOOD_FALLBACK.odMin, odMax: blood.odMax ?? BLOOD_FALLBACK.odMax, lut: blood.lut,
      lutRange: blood.lutRange, opacity: blood.opacity,
    }) : null),
    [heatFieldValue, blood.odMin, blood.odMax, blood.lut, blood.opacity],
  );
  // Noms des canaux calculés : renommés (comparison.labels, enregistrés tout de suite), sinon par défaut.
  const labels = sample?.comparison?.labels || {};
  const computedName = id => labels[id] || (id === BLOOD_ID ? 'Blood (absorbance)' : id === VHE_ID ? 'Virtual HE'
    : id === NUCLEI_ID ? 'Nuclei (hematoxylin)' : id === REDOX_ID ? 'Redox ratio' : id === COMPOSITE_ID ? 'RGB composite'
      : id === COLLAGEN_ID ? 'Collagen fiber orientation' : id === ATTENTION_ID ? 'HES + attention map'
      : compare.op === 'diff' ? 'Difference A − B' : 'Ratio A / B');
  const activeName = derivedShown ? computedName(DERIVED_ID) : bloodShown ? computedName(BLOOD_ID)
    : vheShown ? computedName(VHE_ID) : nucleiShown ? computedName(NUCLEI_ID) : redoxShown ? computedName(REDOX_ID)
      : compositeShown ? computedName(COMPOSITE_ID) : collagenShown ? computedName(COLLAGEN_ID)
        : attentionShown ? computedName(ATTENTION_ID) : activeChannel?.name;
  // k et maximum affiché estimés à la première ouverture du canal sang (tant qu'ils ne sont pas réglés).
  useEffect(() => {
    if (bloodShown && blood.k == null) estimateBloodNow().catch(err => console.error('Blood channel:', err));
  }, [bloodShown, blood.k == null, !!bloodCal]);

  // Rideau : « Comparer au HE », modalité affichée au-dessus du HE.
  const modalityShown = !!(derivedShown || bloodShown || vheShown || nucleiShown || redoxShown || compositeShown || collagenShown
    || attentionShown || activeChannel);
  const canCompare = he.length > 0 && modalityShown;
  const curtainActive = canCompare && compareHe;

  // ------------------------------------------------------------ gomme
  // Cible : la modalité active (fluorescence ou canal calculé), sinon le HE. Zones d'une modalité gardées en cache tant
  // que la liste ne change pas (évite de retrianguler les polygones à chaque rendu).
  const eraseTarget = COMPUTED.includes(active) ? active : activeChannel ? activeChannel.id : he[0]?.id ?? null;
  // Fond affiché d'une modalité (couleur des caches) : image, couleur de sa LUT (inversion comprise) au niveau du fond,
  // le haut du contraste pour une transmission (verre clair), le bas pour une fluorescence ; HE, HE virtuel et noyaux :
  // blanc ; rapport A / B : blanc s'il est inversé ; sang, redox : noir. null : modalité inconnue.
  const bgOf = ch => lutColor(ch.lut, ch.color, ch.inverted, isTrans(ch) ? 1 : 0, ch.lutRange).map(Math.round);
  const backgroundOf = id => {
    if (id === DERIVED_ID) return compare.inverted ? WHITE : BLACK;
    if (id === VHE_ID || id === NUCLEI_ID || id === ATTENTION_ID) return WHITE;
    if (id === BLOOD_ID || id === REDOX_ID || id === COMPOSITE_ID) return BLACK;
    if (id === COLLAGEN_ID) return chCol ? bgOf(chCol) : BLACK;
    const ch = channels.find(c => c.id === id);
    return ch ? (ch.rgb ? WHITE : bgOf(ch)) : null;
  };
  // Fond sombre ou clair : une zone effacée cache toutes les modalités au fond du même type que celle où elle a été
  // tracée ou détectée (bg, fixé à la création ; zones antérieures : fond actuel de leur modalité t, sans t : toutes).
  // Le HES (image RGB) et le canal HES + carte d'attention sont à part (demande du 2026-11-03) : leurs zones ne
  // cachent qu'eux deux et celles des modalités sur fond clair ne les cachent plus, y compris les zones tracées sur eux
  // avant (enregistrées avec bg 'light').
  const isHe = id => id === ATTENTION_ID || he.some(ch => ch.id === id);
  const bgClassOf = id => {
    if (isHe(id)) return 'he';
    const c = backgroundOf(id);
    return c ? (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2] >= 128 ? 'light' : 'dark') : null;
  };
  const zoneClassOf = z => (isHe(z.t) ? 'he' : z.bg ?? (z.t == null ? 'all' : bgClassOf(z.t)));
  // Zones d'un type de fond gardées en cache tant que la liste et le fond des modalités d'origine ne changent pas
  // (évite de retrianguler les polygones à chaque rendu).
  const zonesCache = useRef({ erase: null, sig: '', byClass: new Map() });
  const zonesOf = id => {
    const cls = bgClassOf(id);
    const sig = [...new Set(erase.filter(z => !z.bg || isHe(z.t)).map(z => z.t))]
      .map(t => `${t}:${bgClassOf(t)}`).join('|');
    const cache = zonesCache.current;
    if (cache.erase !== erase || cache.sig !== sig) { cache.erase = erase; cache.sig = sig; cache.byClass = new Map(); }
    if (!cache.byClass.has(cls)) {
      cache.byClass.set(cls, erase.filter(z => { const c = zoneClassOf(z); return c === 'all' || c === cls; }));
    }
    return cache.byClass.get(cls);
  };
  const targetZones = zonesOf(eraseTarget);
  const targetClass = bgClassOf(eraseTarget);
  const targetName = COMPUTED.includes(eraseTarget) ? activeName : channels.find(c => c.id === eraseTarget)?.name;
  const canDetect = (COMPUTED.includes(eraseTarget) && eraseTarget !== ATTENTION_ID) || fluo.some(ch => ch.id === eraseTarget);
  useEffect(() => setEraseNote(null), [eraseTarget]);  // le bilan de détection concerne la modalité précédente
  // Tracé à main levée (glisser) : un point tous les 3 px d'écran, simplifié à la fin ; clic sur une zone : restaurée.
  const eraseDragStart = info => {
    if (!info.coordinate) return;
    strokeRef.current = { points: [info.coordinate.slice(0, 2)], last: [info.x, info.y] };
    setStroke(strokeRef.current.points);
  };
  const eraseDrag = info => {
    const st = strokeRef.current;
    if (!st || !info.coordinate || Math.hypot(info.x - st.last[0], info.y - st.last[1]) < 3) return;
    st.points.push(info.coordinate.slice(0, 2));
    st.last = [info.x, info.y];
    setStroke(st.points.slice());
  };
  const eraseDragEnd = () => {
    const st = strokeRef.current;
    strokeRef.current = null;
    setStroke(null);
    if (!st || st.points.length < 3) return;
    const pxWorld = 2 ** -viewStateRef.current.zoom;  // taille d'un px d'écran en px image
    const zone = simplifyPath(st.points, 1.5 * pxWorld);
    if (zone.length < 3 || polygonArea(zone) < 25 * pxWorld * pxWorld) return;  // simple clic ou trait
    setErase(list => [...list, { p: zone, auto: false, t: eraseTarget, bg: targetClass, px: true }]);
    setSaveState(null);
  };
  const eraseClick = info => {
    if (!info.coordinate) return;
    const w = info.coordinate.slice(0, 2);
    const hit = [...targetZones].reverse().find(z => pointInPolygon(z.p, ...w));
    if (!hit) return;
    setErase(list => list.filter(z => z !== hit));
    setSaveState(null);
  };
  eraseHandlers.current = eraseEdit && !panKey
    ? { dragStart: eraseDragStart, drag: eraseDrag, dragEnd: eraseDragEnd, click: eraseClick }
    : eraseEdit ? { click: eraseClick } : {};
  // Maj enfoncée en mode gomme : glisser déplace la vue au lieu de tracer.
  useEffect(() => {
    if (!eraseEdit) return undefined;
    const down = e => { if (e.key === 'Shift') setPanKey(true); };
    const up = e => { if (e.key === 'Shift') setPanKey(false); };
    const blur = () => setPanKey(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); window.removeEventListener('blur', blur); };
  }, [eraseEdit]);
  // Détection automatique sur la modalité active (A et B pour le canal calculé) ; remplace les zones détectées
  // précédemment pour cette modalité, garde celles tracées à la main.
  const detectErase = async () => {
    const target = eraseTarget;
    setDetecting(true);
    setEraseNote(null);
    try {
      const candidates = (target === DERIVED_ID ? [chA, chB] : target === BLOOD_ID ? [chS, chR]
        : target === VHE_ID ? [chN, chNR, chE, chC] : target === NUCLEI_ID ? [chH, chHR] : target === REDOX_ID ? [chRN, chRF]
          : target === COMPOSITE_ID ? compChans
          : target === COLLAGEN_ID ? [chCol]
          : fluo.filter(ch => ch.id === target))
        .filter(Boolean);
      const ref = candidates[0];
      if (!ref) throw new Error('detection uses the fluorescence images: turn on a fluorescence image or the computed channel.');
      const size = pixelSize(ref.loader[0]);
      const sources = candidates.filter(ch => {
        const s = pixelSize(ch.loader[0]);
        return s.width === size.width && s.height === size.height;
      });
      // Tissu clair sur fond sombre : contraste retourné pour une transmission (verre clair, tissu sombre), quel que soit
      // l'affichage (la détection lit les valeurs, pas les couleurs).
      const reversed = ch => isTrans(ch);
      const { zones, kept } = await detectDust(sources.map(ch => ({
        loader: ch.loader, contrast: reversed(ch) ? [...ch.contrast].reverse() : ch.contrast,
      })));
      // Zones dans les pixels de la source (ceux de la cible : la fluorescence, ou A pour le canal calculé).
      const bg = bgClassOf(target);
      setErase(list => [...list.filter(z => !(z.auto && z.t === target)), ...zones.map(p => ({ p, auto: true, t: target, bg, px: true }))]);
      setEraseNote(`${zones.length} object${zones.length > 1 ? 's' : ''} erased around ${kept} tissue block${kept > 1 ? 's' : ''}`);
      setSaveState(null);
    } catch (err) {
      setEraseNote(`Failed: ${err.message}`);
    } finally {
      setDetecting(false);
    }
  };

  // Extension de shader par canal, recréée seulement quand sa LUT ou son inversion change.
  const extensionFor = ch => {
    const key = `${ch.lut}|${ch.inverted}`;
    const cached = extensionsRef.current[ch.id];
    if (cached?.key === key) return cached.extensions;
    const extensions = [new LutExtension({ luts: [ch.lut], inverted: [ch.inverted] })];
    extensionsRef.current[ch.id] = { key, extensions };
    return extensions;
  };

  // Vue partagée par les deux canevas (rideau) : tout zoom ou déplacement, d'un côté ou de l'autre, est appliqué aux
  // deux.
  const syncViewState = ({ viewState }) => {
    viewStateRef.current = viewState;
    deckRef.current?.setProps({ viewState });
    deck2Ref.current?.setProps({ viewState });
    viewListeners.current.forEach(listener => listener());
    return viewState;
  };
  const subscribeView = useCallback(listener => {
    viewListeners.current.add(listener);
    return () => viewListeners.current.delete(listener);
  }, []);
  // Vecteurs du collagène : niveau de blocs recalculé à chaque changement de vue (rendu seulement s'il change).
  useEffect(() => {
    if (!colBlocks) return undefined;
    const update = () => {
      const zoom = viewStateRef.current?.zoom;
      if (zoom != null) setVecLevel(vectorLevel(colBlocks, colReady.cell, Array.isArray(zoom) ? zoom[0] : zoom));
    };
    update();
    return subscribeView(update);
  }, [colBlocks]);

  // Clics et glisser sur l'image, pour les deux canevas : gomme (tracé, restauration). Lus dans des refs : les canevas
  // sont créés une fois.
  const pointerHandlers = {
    onClick: info => eraseHandlers.current.click?.(info),
    onDragStart: info => eraseHandlers.current.dragStart?.(info),
    onDrag: info => eraseHandlers.current.drag?.(info),
    onDragEnd: info => eraseHandlers.current.dragEnd?.(info),
    getCursor: ({ isDragging }) => {
      if (eraseHandlers.current.dragStart) return 'crosshair';
      return isDragging ? 'grabbing' : 'grab';
    },
  };

  // Création du canevas deck.gl au premier canal chargé, puis mise à jour des couches.
  useEffect(() => {
    if (!channels.length) {
      deckRef.current?.setProps({ layers: [] });  // dernière modalité supprimée
      return;
    }
    if (!containerRef.current) return;
    if (!deckRef.current) {
      const el = containerRef.current;
      // Vue initiale : l'image entière, ou le rectangle gardé s'il y a un recadrage enregistré.
      viewStateRef.current = crop
        ? fitView(crop, el.clientWidth, el.clientHeight)
        : getDefaultInitialViewState((he[0] || channels[0]).loader, { width: el.clientWidth, height: el.clientHeight }, 0.5);
      deckRef.current = new Deck({
        parent: el,
        views: [new OrthographicView({ id: 'ortho', controller: true })],
        viewState: viewStateRef.current,
        onViewStateChange: syncViewState,
        controller: true,
        layers: [],
        ...pointerHandlers,
        onError: err => console.error('Viewer (deck.gl):', err),
      });
      if (import.meta.env.DEV) {  // inspection en développement
        window.hyprviewDeck = deckRef.current;
        window.hyprviewSetView = viewState => syncViewState({ viewState: { ...viewStateRef.current, ...viewState } });
      }
    }
    // Image couleur (HES…) : toujours affichée, mélange normal, opacité et saturation réglables.
    const rgbLayer = ch => new MultiscaleImageLayer({
      id: `image-${ch.id}`,
      loader: ch.loader,
      selections: [{ t: 0, z: 0, c: 0 }],
      contrastLimits: [[0, 255]],
      colors: [[255, 255, 255]],
      channelsVisible: [true],
      opacity: ch.opacity,
      saturation: ch.saturation,
      photometric: ch.loader[0].meta?.photometricInterpretation,
      renderSubLayers: renderRgbTile,
      maxRequests: 24,
      excludeBackground: true,
      refinementStrategy: 'no-overlap',
      parameters: NORMAL,
    });
    // Fluorescence : LUT, contraste, opacité, flou.
    const fluoLayer = ch => new MultiscaleImageLayer({
      id: `image-${ch.id}`,
      loader: loaderFor(ch),
      selections: [{ t: 0, z: 0, c: 0 }],
      contrastLimits: [ch.contrast],
      colors: [ch.color],
      channelsVisible: [true],
      opacity: ch.opacity,
      extensions: extensionFor(ch),
      lutRanges: [ch.lutRange || [0, 1]],  // partie de la LUT utilisée (uniformes de LutExtension)
      maxRequests: 24,                  // tuiles téléchargées en parallèle (HTTP/2)
      excludeBackground: true,          // évite la double contribution fond basse résolution + tuiles en mode additif
      refinementStrategy: 'no-overlap',
      parameters: ADDITIVE,
    });
    // Modalité active : fluorescence ou canal calculé.
    let activeLayer = null;
    if (derivedShown) {
      activeLayer = new MultiscaleImageLayer({
        id: `derived-${chA.id}-${chB.id}`,
        loader: pair.loader,
        selections: [{ t: 0, z: 0, c: 0 }, { t: 0, z: 0, c: 1 }],
        contrastLimits: [chA.contrast, chB.contrast],
        colors: [[255, 255, 255], [255, 255, 255]],
        channelsVisible: [true, true],
        extensions: diffExtension,
        derived: derivedSettings,
        renderSubLayers: renderLinearTile,
        maxRequests: 24,
        excludeBackground: true,
        refinementStrategy: 'no-overlap',
      });
    } else if (bloodShown) {
      activeLayer = new MultiscaleImageLayer({
        id: `blood-${chS.id}-${chR.id}`,
        loader: bloodPair.loader,
        selections: [{ t: 0, z: 0, c: 0 }, { t: 0, z: 0, c: 1 }],
        contrastLimits: [bloodCal.s, bloodCal.r],  // [noir, verre] : intensité lue = transmittance
        colors: [[255, 255, 255], [255, 255, 255]],
        channelsVisible: [true, true],
        extensions: bloodExtension,
        blood,
        renderSubLayers: renderLinearTile,
        maxRequests: 24,
        excludeBackground: true,
        refinementStrategy: 'no-overlap',
      });
    } else if (vheShown) {
      activeLayer = new MultiscaleImageLayer({
        id: `vhe-${chN.id}-${chNR.id}-${chE.id}${chC ? `-${chC.id}` : ''}`,
        loader: vheStack.loader,
        selections: (chC ? [0, 1, 2, 3] : [0, 1, 2]).map(c => ({ t: 0, z: 0, c })),
        // transmittances (contraste [noir, verre]) des noyaux et de la référence ; éosine et collagène : fenêtres
        // estimées (fond -> signal), sinon contraste de leur carte
        contrastLimits: [
          vheCal.n, vheCal.r, vhe.eLow != null ? [vhe.eLow, vhe.eHigh] : chE.contrast,
          ...(chC ? [vhe.cLow != null ? [vhe.cLow, vhe.cHigh] : chC.contrast] : []),
        ],
        colors: (chC ? [0, 1, 2, 3] : [0, 1, 2]).map(() => [255, 255, 255]),
        channelsVisible: (chC ? [0, 1, 2, 3] : [0, 1, 2]).map(() => true),
        extensions: vheExtension,
        vhe,
        renderSubLayers: renderLinearTile,
        maxRequests: 24,
        excludeBackground: true,
        refinementStrategy: 'no-overlap',
      });
    } else if (nucleiShown) {
      // Noyaux seuls : transmittances (contraste [noir, verre]) à 265 nm et de la référence, hématoxyline du HE virtuel
      activeLayer = new MultiscaleImageLayer({
        id: `nuclei-${chH.id}-${chHR.id}`,
        loader: nucStack.loader,
        selections: [0, 1].map(c => ({ t: 0, z: 0, c })),
        contrastLimits: [nucCal.n, nucCal.r],
        colors: [[255, 255, 255], [255, 255, 255]],
        channelsVisible: [true, true],
        extensions: nucExtension,
        vhe: nuclei,
        renderSubLayers: renderLinearTile,
        maxRequests: 24,
        excludeBackground: true,
        refinementStrategy: 'no-overlap',
      });
    } else if (redoxShown) {
      // NAD(P)H et FAD en comptes d'origine, fond retiré : contraste [fond, fond + 1 compte] (Viv ne borne pas le haut)
      const counts = (ch, bg) => [fromOriginal(ch, bg), fromOriginal(ch, bg + 1)];
      activeLayer = new MultiscaleImageLayer({
        id: `redox-${chRN.id}-${chRF.id}`,
        loader: redoxStack.loader,
        selections: [0, 1].map(c => ({ t: 0, z: 0, c })),
        contrastLimits: [counts(chRN, redoxResolved.bgN), counts(chRF, redoxResolved.bgF)],
        colors: [[255, 255, 255], [255, 255, 255]],
        channelsVisible: [true, true],
        extensions: redoxExtension,
        redox: redoxResolved,
        renderSubLayers: renderLinearTile,
        maxRequests: 24,
        excludeBackground: true,
        refinementStrategy: 'no-overlap',
      });
    } else if (compositeShown) {
      // Composite RGB : fenêtre de chaque couleur (contraste de la carte, minimum, gain), puis transformation (identité
      // ou décorrélation)
      activeLayer = new MultiscaleImageLayer({
        id: `composite-${compChans.map(ch => ch.id).join('-')}`,
        loader: compositeStack.loader,
        selections: [0, 1, 2].map(c => ({ t: 0, z: 0, c })),
        contrastLimits: compWindows,
        colors: COMPOSITE_KEYS.map(key => COMPOSITE_COLORS[key]),
        channelsVisible: [true, true, true],
        extensions: compositeExtension,
        composite: compTransform,  // identité en direct, décorrélation sinon
        renderSubLayers: renderLinearTile,
        maxRequests: 24,
        excludeBackground: true,
        refinementStrategy: 'no-overlap',
      });
    } else if (collagenShown) {
      // SHG tel qu'affiché sur sa carte, orientation au-dessus (calque « overlay »)
      activeLayer = fluoLayer(chCol);
    } else if (attentionShown) {
      // HES en pleine résolution, carte d'attention au-dessus (calque « overlay »)
      activeLayer = rgbLayer(heCh);
    } else if (activeChannel) {
      activeLayer = fluoLayer(activeChannel);
    }
    // Carte de chaleur du sang, au-dessus du fond gris : une image par maille, étirée sur l'image (interpolation
    // linéaire).
    const bloodOverlay = bloodShown && heatmap && heatImage ? [new BitmapLayer({
      id: `blood-heat-${heatKey}`,
      image: heatImage,
      bounds: [0, heatData.data.height * heatData.data.cell, heatData.data.width * heatData.data.cell, 0],
      textureParameters: { minFilter: 'linear', magFilter: 'linear' },
      parameters: NORMAL,
    })] : [];
    // Orientation du collagène au-dessus du SHG : une image par px d'analyse, étirée sur l'image.
    const collagenOverlay = collagenShown && colImage ? [new BitmapLayer({
      id: `collagen-${colKey}`,
      image: colImage,
      bounds: [0, colReady.height * colReady.cell, colReady.width * colReady.cell, 0],
      textureParameters: { minFilter: 'linear', magFilter: 'nearest' },
      parameters: NORMAL,
    })] : [];
    // Vecteurs : traits sans flèche, liseré sombre dessous pour rester lisibles sur le SHG et sur la carte.
    const vectorLayer = (id, color, width) => new LineLayer({
      id: `collagen-vectors-${id}`,
      data: color ? colVectorData.plain : colVectorData.colored,
      ...(color ? { getColor: color } : {}),
      getWidth: width,
      widthUnits: 'pixels',
      parameters: NORMAL,
      updateTriggers: { getColor: color?.join() },
    });
    const collagenVectorLayers = collagenShown && collagen.vectors && colVectorData ? [
      vectorLayer('halo', [0, 0, 0, 170], collagen.vecWidth + 2),
      vectorLayer('lines', collagen.vecColor === 'orientation' ? null : [255, 255, 255, 255], collagen.vecWidth),
    ] : [];
    // Carte d'attention au-dessus du HES : une image par px du niveau réduit, étirée sur l'image (patchs bruts : sans
    // interpolation).
    const attentionOverlay = attentionShown && attImage ? [new BitmapLayer({
      id: `attention-${attMap.id}`,
      image: attImage,
      bounds: [0, attFieldValue.height * attFieldValue.sy, attFieldValue.width * attFieldValue.sx, 0],
      textureParameters: { minFilter: 'linear', magFilter: attention.sigma > 0 ? 'linear' : 'nearest' },
      parameters: NORMAL,
    })] : [];
    // Isolignes : traits vectoriels (nets à tous les zooms et dans l'export), largeur en px d'écran, liseré sombre
    // dessous pour rester lisibles sur le rose pâle comme sur le violet du HES.
    const contourData = attentionShown && attContours
      ? attContours.flatMap(({ level, paths }) => paths.map(path => ({ path, level }))) : [];
    const contourLayer = (id, width, color) => new PathLayer({
      id: `attention-contours-${id}`,
      data: contourData,
      getPath: d => d.path,
      getColor: color || (d => contourColor(d.level)),
      getWidth: width,
      widthUnits: 'pixels',
      jointRounded: true,
      capRounded: true,
      parameters: NORMAL,
      updateTriggers: { getColor: [attShown.lut, attShown.lutRange?.join(), attention.opacity, attSigned, attView.zero] },
    });
    const attentionContourLayers = contourData.length ? [
      contourLayer('halo', attention.lineWidth + 2, [0, 0, 0, Math.round(120 * attention.opacity)]),
      contourLayer('lines', attention.lineWidth),
    ] : [];
    const overlay = [...bloodOverlay, ...collagenOverlay, ...collagenVectorLayers, ...attentionOverlay, ...attentionContourLayers];
    // Couleur des caches (recadrage, gomme) : celle du fond de la modalité cachée (backgroundOf).
    const activeBg = backgroundOf(derivedShown ? DERIVED_ID : bloodShown ? BLOOD_ID : vheShown ? VHE_ID
      : nucleiShown ? NUCLEI_ID : redoxShown ? REDOX_ID : compositeShown ? COMPOSITE_ID : collagenShown ? COLLAGEN_ID
        : attentionShown ? ATTENTION_ID : activeChannel?.id) || BLACK;
    const deck1Bg = he.length ? WHITE : activeBg;
    // Gomme : zones effacées (contour rouge et semi-transparentes en mode gomme), tracé en cours.
    const eraseLayers = (color, target) => [
      ...(zonesOf(target).length ? [new PolygonLayer({
        id: 'erase-zones',
        data: zonesOf(target),
        getPolygon: d => d.p,
        getFillColor: [...color, eraseEdit ? 150 : 255],
        stroked: eraseEdit,
        getLineColor: [255, 90, 90],
        lineWidthUnits: 'pixels',
        getLineWidth: 1.5,
        updateTriggers: { getFillColor: [eraseEdit, color.join()] },
        parameters: NORMAL,
      })] : []),
      ...(stroke ? [new PathLayer({
        id: 'erase-stroke', data: [stroke], getPath: d => d, getColor: [255, 90, 90], widthUnits: 'pixels', getWidth: 2,
      })] : []),
    ];
    // Recadrage : cache hors du rectangle gardé, au-dessus des images (semi-transparent pendant le réglage, pour voir ce
    // qui est masqué). Une instance par canevas.
    const { width: iw, height: ih } = pixelSize((he[0] || channels[0]).loader[0]);
    // Modalité affichée dans la vue du dessus (rideau) ou seule : ses zones effacées.
    const shownTarget = derivedShown ? DERIVED_ID : bloodShown ? BLOOD_ID : vheShown ? VHE_ID : nucleiShown ? NUCLEI_ID
      : redoxShown ? REDOX_ID : compositeShown ? COMPOSITE_ID : collagenShown ? COLLAGEN_ID
        : attentionShown ? ATTENTION_ID : activeChannel?.id ?? null;
    const heTarget = he[0]?.id ?? null;
    const mask = (color = BLACK, target = null) => (crop ? [new SolidPolygonLayer({
      id: 'crop-mask',
      data: [{ polygon: [
        [[-iw, -ih], [2 * iw, -ih], [2 * iw, 2 * ih], [-iw, 2 * ih]],
        [[crop.x0, crop.y0], [crop.x1, crop.y0], [crop.x1, crop.y1], [crop.x0, crop.y1]],
      ] }],
      getPolygon: d => d.polygon,
      getFillColor: [...color, cropEdit ? 170 : 255],
      updateTriggers: { getPolygon: [crop.x0, crop.y0, crop.x1, crop.y1], getFillColor: [cropEdit, color.join()] },
      parameters: NORMAL,
    })] : []).concat(eraseLayers(color, target));
    const controller = eraseEdit && !panKey ? ERASE_CONTROLLER : true;
    deckRef.current.setProps({ controller });
    deck2Ref.current?.setProps({ controller });
    // Export pleine résolution : ce que montre la vue (la modalité active, sinon le HE), sans le rideau.
    exportRef.current = activeLayer
      ? { layers: [activeLayer, ...overlay, ...mask(activeBg, shownTarget)], background: activeBg }
      : { layers: [...he.map(rgbLayer), ...mask(deck1Bg, heTarget)], background: deck1Bg };
    if (curtainActive) {
      // Rideau : HE dans la vue principale, modalité active dans la vue du dessus (découpée par CSS).
      deckRef.current.setProps({ layers: [...he.map(rgbLayer), ...mask(deck1Bg, heTarget)] });
      if (!deck2Ref.current && curtainRef.current) {
        deck2Ref.current = new Deck({
          parent: curtainRef.current,
          views: [new OrthographicView({ id: 'ortho', controller: true })],
          viewState: viewStateRef.current,
          onViewStateChange: syncViewState,
          controller: true,
          layers: [],
          ...pointerHandlers,
          onError: err => console.error('Viewer (deck.gl, curtain):', err),
        });
        if (import.meta.env.DEV) window.hyprviewDeck2 = deck2Ref.current;
      }
      deck2Ref.current?.setProps({ layers: [activeLayer, ...overlay, ...mask(activeBg, shownTarget)] });
    } else {
      // Sans rideau : la modalité active seule, sinon le HE.
      deckRef.current.setProps({ layers: activeLayer
        ? [activeLayer, ...overlay, ...mask(activeBg, shownTarget)]
        : [...he.map(rgbLayer), ...mask(deck1Bg, heTarget)] });
      deck2Ref.current?.setProps({ layers: [] });
    }
  });

  useEffect(() => () => {
    deckRef.current?.finalize(); deckRef.current = null;
    deck2Ref.current?.finalize(); deck2Ref.current = null;
  }, []);

  // Rideau centré sur la partie visible du scan : rectangle gardé (recadrage), sinon image de la modalité active,
  // intersecté avec la vue ; milieu de la vue si le scan n'y est pas.
  const centerCurtain = () => {
    const el = containerRef.current;
    const view = viewStateRef.current;
    const shown = derivedShown ? chA : bloodShown ? chS : vheShown ? chN : nucleiShown ? chH : redoxShown ? chRN
      : compositeShown ? compChans[0]
      : collagenShown ? chCol : attentionShown ? heCh : activeChannel;
    if (!el || !view || !shown) return;
    let xs;
    if (crop) {
      xs = [crop.x0, crop.x1];
    } else {
      xs = [0, pixelSize(shown.loader[0]).width];
    }
    const toScreen = x => el.clientWidth / 2 + (x - view.target[0]) * 2 ** view.zoom;
    const left = Math.max(0, toScreen(Math.min(...xs)));
    const right = Math.min(el.clientWidth, toScreen(Math.max(...xs)));
    setCurtainPos(right > left ? Math.min(0.98, Math.max(0.02, (left + right) / 2 / el.clientWidth)) : 0.5);
  };

  // Déplacement de la poignée du rideau (position en fraction de la largeur du canevas).
  const dragCurtain = e => {
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const move = ev => {
      const pos = Math.min(0.98, Math.max(0.02, (ev.clientX - rect.left) / rect.width));
      setCurtainPos(pos);
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Réorganisation : déplace une carte avant ou après une autre (glisser-déposer), ou d'un cran (flèches du clavier).
  const placeModality = ids => { setOrder(ids); setSaveState(null); };
  // Rangement d'une carte dans les canaux de calcul (inStash) ou dans la liste principale.
  const setSection = (id, inStash) => {
    setStash(prev => (inStash ? [...prev.filter(x => x !== id), id] : prev.filter(x => x !== id)));
    if (inStash) setStashOpen(true);
    setSaveState(null);
  };
  // Déposée sur une autre carte : à sa place, et dans sa section.
  const moveModality = (id, targetId, after) => {
    if (id === targetId) return;
    const ids = cards.map(c => c.id).filter(x => x !== id);
    ids.splice(ids.indexOf(targetId) + (after ? 1 : 0), 0, id);
    placeModality(ids);
    if (stash.includes(targetId) !== stash.includes(id)) setSection(id, stash.includes(targetId));
  };
  // Déposée sur le titre d'une section : rangée dans cette section.
  const sectionDragOver = (e, section) => {
    if (!dragId) return;
    e.preventDefault();
    if (dropSection !== section) setDropSection(section);
  };
  const sectionDrop = (e, inStash) => {
    e.preventDefault();
    if (dragId) setSection(dragId, inStash);
    endDrag();
  };
  const stepModality = (id, delta) => {
    const ids = cards.map(c => c.id);
    const k = ids.indexOf(id);
    if (k + delta < 0 || k + delta >= ids.length) return;
    [ids[k], ids[k + delta]] = [ids[k + delta], ids[k]];
    placeModality(ids);
    // La carte déplacée vers le bas est réinsérée dans le DOM : on lui rend le focus.
    requestAnimationFrame(() => document.querySelector(`#viewer-page [data-drag="${id}"]`)?.focus());
  };
  const startDrag = (e, id) => {
    const card = e.currentTarget.parentElement;
    const rect = card.getBoundingClientRect();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);  // requis par Firefox pour démarrer le glisser
    e.dataTransfer.setDragImage(card, e.clientX - rect.left, e.clientY - rect.top);
    setDragId(id);
  };
  const endDrag = () => { setDragId(null); setDropAt(null); setDropSection(null); };
  const dragOver = (e, id) => {
    if (!dragId) return;  // autre chose qu'une carte (fichier…)
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > rect.top + rect.height / 2;
    setDropAt(prev => (prev?.id === id && prev.after === after ? prev : { id, after }));
  };
  const drop = e => {
    e.preventDefault();
    if (dragId && dropAt) moveModality(dragId, dropAt.id, dropAt.after);
    endDrag();
  };

  const toggleCard = id => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const update = (id, patch) => {
    const { visible, ...rest } = patch;
    // Interrupteur d'une modalité : l'activer désactive les autres (une seule face au HE).
    if (visible !== undefined) setActive(visible ? id : null);
    if (Object.keys(rest).length) setSettings(prev => ({ ...prev, [id]: { ...prev[id], ...rest } }));
    setSaveState(null);
  };

  // Présentation (lecture seule) : démo ouverte par qui ne peut pas la modifier, ou compte externe. Interrupteurs
  // seuls : ni réglages, ni canaux de calcul, ni outils (recadrage, gomme, téléchargement), ni renommage.
  const presentation = !canWrite && (!!demo || profile?.role === 'external');
  // Carte repliée / dépliable ; en présentation : en-tête seul.
  const fold = id => (presentation ? { collapsed: true } : { collapsed: !expanded[id], onCollapse: () => toggleCard(id) });
  // Démo (administrateurs) : l'échantillon devient lisible par tous les comptes, externes compris.
  const toggleDemo = on => {
    if (on && !window.confirm(`Make "${sample.name}" a demo?\n\nEvery active account, external ones included, `
      + 'will be able to open it read-only (displayed modalities, without the computation channels or the settings).')) return;
    setDemo(profile, projectId, sampleId, on ? sample.name : null)
      .catch(err => window.alert(`Failed: ${err.message}`));
  };
  const saveDefaults = async () => {
    setSaveState('saving');
    try {
      const { op, gain, balance, context, mask, threshold, inverted } = compare;
      await saveSampleComparison(projectId, sampleId, {
        op, a: chA?.id ?? null, b: chB?.id ?? null, gain, balance, smooth, context, mask, threshold, inverted,
        blood: {
          s: chS?.id ?? null, r: chR?.id ?? null, k: blood.k, odMin: blood.odMin, odMax: blood.odMax, context: blood.context, lut: blood.lut,
          dark: blood.dark, display: blood.display, heatLevel: blood.heatLevel, heatSmooth: blood.heatSmooth,
          opacity: blood.opacity, lutRange: blood.lutRange ?? null,
        },
        collagen: { ...Object.fromEntries(Object.keys(COLLAGEN_DEFAULTS).map(key => [key, collagen[key] ?? null])), c: chCol?.id ?? null },
        attention: { ...Object.fromEntries(Object.keys(ATTENTION_DEFAULTS).map(key => [key, attention[key] ?? null])), o: attMap?.id ?? null },
        vhe: {
          n: chN?.id ?? null, r: chNR?.id ?? null, e: chE?.id ?? null, c: chC ? chC.id : vhe.c === 'none' ? 'none' : null,
          k: vhe.k, hScale: vhe.hScale, eLow: vhe.eLow, eHigh: vhe.eHigh, cLow: vhe.cLow, cHigh: vhe.cHigh,
          hGain: vhe.hGain, eGain: vhe.eGain, sGain: vhe.sGain,
        },
        nuclei: { n: chH?.id ?? null, r: chHR?.id ?? null, k: nuclei.k, hScale: nuclei.hScale, hGain: nuclei.hGain },
        redox: {
          ...Object.fromEntries(Object.keys(REDOX_DEFAULTS).map(key => [key, redox[key] ?? null])),
          n: chRN?.id ?? null, f: chRF?.id ?? null,
        },
        composite: { ...composite, ...Object.fromEntries(COMPOSITE_KEYS.map((key, i) => [key, compChans[i]?.id ?? null])) },
        active: active ?? null,
        curtainPos,
        curtain: compareHe,
        order: cards.map(c => c.id), crop,
        stash: stash.filter(id => cards.some(c => c.id === id)),
        labels,  // renommages, enregistrés à part : repris tels quels
        // Zones : type de fond fixé (celui actuel de leur modalité pour les zones antérieures).
        erase: erase.filter(z => z.bg || z.t == null || COMPUTED.includes(z.t) || (images || []).some(img => img.id === z.t))
          .map(z => {
            const bg = zoneClassOf(z);
            return { p: z.p.flat().map(Math.round), auto: !!z.auto, t: z.t ?? null, ...(bg && bg !== 'all' ? { bg } : {}), px: !!z.px };
          }),
      });
      await Promise.all(channels.map(ch => saveImageDisplay(projectId, sampleId, ch.id, ch.rgb ? { opacity: ch.opacity, saturation: ch.saturation } : {
        color: ch.color, lut: ch.lut, inverted: ch.inverted, contrast: ch.contrast, opacity: ch.opacity, blur: ch.blur || 0,
        ...(ch.tiled ? { contrastUnit: 'tiles' } : {}), ...(ch.sourceWindow ? { sourceWindow: ch.sourceWindow } : {}),
        ...(ch.lutRange ? { lutRange: ch.lutRange } : {}),
      })));
      setSaveState('saved');
    } catch (err) {
      setSaveState(`Failed: ${err.message}`);
    }
  };

  const failures = visibleImages.filter(img => loaded[img.id]?.error);
  const pending = visibleImages.filter(img => loaded[img.id]?.loading).length;
  // Repère de l'image (dimensions affichées, recadrage) : le HE, sinon la première modalité chargée.
  const first = he[0] || channels[0];
  const { height: H, width: W } = first ? pixelSize(first.loader[0]) : {};

  // Échelle : taille de pixel de la grille commune, réglée sur l'échantillon (metadata.pixelSizeUm), sinon celle d'un
  // fichier (relevée à l'envoi, ou métadonnées OME d'une image lue directement), image affichée d'abord.
  const shownFirst = [...channels].sort((x, y) => (y.id === active) - (x.id === active));
  const filePixel = shownFirst.map(ch => {
    const img = visibleImages.find(i => i.id === ch.id);
    const meta = ch.loader[0].meta?.physicalSizes?.x;
    return img?.pixelSizeUm || (meta ? toMicrons(meta.size, meta.unit) : null);
  }).find(v => v > 0) || null;
  const samplePixel = Number(sample?.metadata?.pixelSizeUm) || null;
  const umPerPx = samplePixel || filePixel;
  const umSource = samplePixel ? 'set on the sample' : 'file metadata';
  // Export : zone gardée (recadrage, sinon l'image entière) et nom de ce qui est affiché.
  const exportRegion = crop ? { ...crop, cropped: true } : first ? { x0: 0, y0: 0, x1: W, y1: H } : null;
  const exportName = activeName || he[0]?.name || 'image';
  if (import.meta.env.DEV) {  // essai sans boîte de dialogue : export en mémoire d'une zone, renvoie le fichier
    window.hyprviewExportTest = async (region, quality = 0.9) => {
      const { exportPyramid } = await import('../Viewer/exportRender');
      const { memorySink } = await import('../Viewer/tiffWriter');
      const sink = memorySink();
      await exportPyramid({ ...exportRef.current, region, sink, quality, name: exportName, pixelSizeUm: umPerPx });
      return sink.blob();
    };
  }
  const setPixel = um => setSamplePixelSize(projectId, sampleId, um)
    .catch(err => window.alert(`Failed to save the pixel size: ${err.message}`));

  // Barre de couleurs de la modalité affichée, quand ses couleurs codent une valeur : intensité d'une fluorescence
  // (unités d'origine si connues), OD du sang, redox ratio, alignement ou orientation du collagène, rapport ou
  // différence A / B. Pas pour le HE ni les colorations virtuelles (couleurs d'une lame, pas une échelle).
  const ticks3 = (lo, hi, fmt = tickLabel) => [0, 0.5, 1].map(t => ({ t, label: fmt(lo + t * (hi - lo)) }));
  let legend = null;
  if (derivedShown) {
    // Couleur pleine à |log2(A / (B × 2^équilibre))| = 4 / gain (rapport, pixels les plus lumineux) ou
    // |A − B × 2^équilibre| = 1 / gain (différence, intensités normalisées par le contraste).
    const ramp = (rgb, k) => (compare.inverted ? rgb.map(v => 255 - k * (255 - v)) : rgb.map(v => k * v));
    const colorAt = t => (t < 0.5 ? ramp(DIFF_COLORS.b, 1 - 2 * t) : ramp(DIFF_COLORS.a, 2 * t - 1));
    const g = compare.gain || 1;
    legend = compare.op === 'diff'
      ? { title: `${chA.name} − ${chB.name} (normalized)`, colorAt,
        ticks: [{ t: 0, label: `−${tickLabel(1 / g)}` }, { t: 0.5, label: '0' }, { t: 1, label: `+${tickLabel(1 / g)}` }] }
      : { title: `${chA.name} / ${chB.name}`, colorAt,
        ticks: [-1, 0, 1].map(k => ({ t: (k + 1) / 2, label: tickLabel(2 ** ((compare.balance || 0) + (4 * k) / g)) })),
        note: 'Full color for the brightest pixels: it is modulated by brightness.' };
  } else if (bloodShown) {
    legend = {
      title: `${computedName(BLOOD_ID)} · OD`, colorAt: t => lutColor(blood.lut, [255, 255, 255], false, t, blood.lutRange),
      ticks: ticks3(blood.odMin ?? BLOOD_FALLBACK.odMin, blood.odMax ?? BLOOD_FALLBACK.odMax),
    };
  } else if (redoxShown) {
    legend = {
      title: REDOX_FORMULAS[redox.formula], colorAt: t => lutColor(redox.lut, [255, 255, 255], false, t, redox.lutRange),
      ticks: ticks3(redoxResolved.lo, redoxResolved.hi),
      note: redox.weighted ? 'Hue: ratio; brightness: total intensity.' : undefined,
    };
  } else if (compositeShown) {
    legend = {
      title: `${computedName(COMPOSITE_ID)}${composite.display === 'decorrelated' ? ' · enhanced differences' : ''}`,
      swatches: COMPOSITE_KEYS.map((key, i) => ({ color: COMPOSITE_COLORS[key], label: compChans[i].name })),
    };
  } else if (collagenShown) {
    if (collagen.display === 'coherence') {
      legend = {
        title: 'Fiber alignment (coherence)', colorAt: t => lutColor(collagen.lut, [255, 255, 255], false, t, collagen.lutRange),
        ticks: ticks3(0, 1),
      };
    } else if (collagen.display === 'orientation' || (collagen.vectors && collagen.vecColor === 'orientation')) {
      legend = { title: 'Fiber orientation', orientation: true };
    }
  } else if (attentionShown && isolines) {
    // Une couleur par isoligne tracée (valeurs signées : du plus négatif au plus positif).
    legend = {
      title: `${computedName(ATTENTION_ID)} · isolines (${attValue}${attSigned ? '' : ' percentile'})`,
      line: true,
      swatches: (attContours || []).filter(c => c.paths.length).map(c => ({
        color: contourColor(c.level).slice(0, 3), label: attLabel(c.level),
      })),
    };
  } else if (attentionShown) {
    // Couleur sur fond blanc, opacité comprise : sous le seuil des points chauds, le HES seul.
    const below = Math.round(attShown.threshold * 100);
    legend = {
      title: `${computedName(ATTENTION_ID)} · ${attValue}${attSigned ? '' : ' percentile'}`,
      colorAt: t => {
        const a = attentionAlpha(t, attView);
        return lutColor(attShown.lut, [255, 255, 255], false, t, attShown.lutRange).map(v => 255 * (1 - a) + v * a);
      },
      // Valeurs signées : bas (seuil ou −échelle), 0, haut.
      ticks: (attSigned ? [...new Set([0, attView.zero, 1])] : [0, 0.5, 1])
        .map(t => ({ t, label: attSigned ? attLabel(t) : String(Math.round(t * 100)) })),
      note: attention.display !== 'hotspots' ? undefined : attSigned
        ? `Transparent below ${below}% of each side of the color scale (from 0).` : `Transparent below the ${below}th percentile.`,
    };
  } else if (activeChannel) {
    const ch = activeChannel;
    const known = !!ch.toOriginal || ch.loader[0].dtype !== 'Uint8';
    const to = ch.toOriginal || (v => v);
    legend = {
      title: `${ch.name} · intensity${known ? ' (counts)' : ''}`,
      colorAt: t => lutColor(ch.lut, ch.color, ch.inverted, t, ch.lutRange), ticks: ticks3(to(ch.contrast[0]), to(ch.contrast[1])),
    };
  }

  let status = null;
  if (error) status = error;
  else if (sample === null) status = "This sample doesn't exist.";
  else if (images && visibleImages.length === 0) {
    status = canWriteProject(profile, project)
      ? 'No images for this sample yet: add one below.'
      : 'No images for this sample.';
  }
  // Images pas encore affichables : envoi en cours ou interrompu (onglet fermé, coupure), découpage en tuiles en
  // cours ou échoué. Leur auteur ou un admin peut les retirer ; un contributeur peut relancer le découpage.
  const unfinished = (images || []).filter(img => ['uploading', 'processing', 'error', 'canceled'].includes(img.status));
  const canRemove = img => !presentation && (profile?.role === 'admin' || img.createdBy === profile?.uid);
  const removeUnfinished = img => {
    if (window.confirm(`Remove "${img.label || img.modality}"?`)) {
      deleteImage(projectId, sampleId, img).catch(err => window.alert(`Failed: ${err.message}`));
    }
  };
  const formatSize = bytes => (bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : `${Math.round(bytes / 1e6)} MB`);
  // Suppression d'une modalité : fiche (réglages), fichier original et, côté serveur, tuiles ; ses traces dans les
  // réglages de l'échantillon (canaux calculés, modalité affichée, ordre, zones effacées) sont retirées.
  const removeImage = async img => {
    const name = img.label || img.modality;
    const size = img.size ? ` (${formatSize(img.size)})` : '';
    if (!window.confirm(`Permanently delete the modality "${name}"?\n\nThe original file${size} and its `
      + 'display tiles will be erased from the cloud for all users. This cannot be undone.')) return;
    try {
      await forgetImageInSample(projectId, sampleId, img.id);
      await deleteImage(projectId, sampleId, img);
    } catch (err) {
      window.alert(`Deletion failed: ${err.message}`);
    }
  };
  const prepare = img => prepareImage(projectId, sampleId, img.id).catch(err => window.alert(`Failed: ${err.message}`));
  // Préparation sans signe de vie depuis 10 min (demande, mise en file ou progression) : bloquée.
  const stalled = img => {
    const last = img.processing?.updatedAt || img.processing?.queuedAt || img.requestedAt;
    return img.status === 'processing' && (!last?.toMillis || Date.now() - last.toMillis() > 10 * 60 * 1000);
  };
  const relaunch = img => restartImagePreparation(projectId, sampleId, img.id)
    .catch(err => window.alert(`Failed: ${err.message}`));
  const unfinishedLabel = img => {
    if (img.status === 'uploading') return 'uploading or interrupted';
    if (stalled(img)) return 'preparation stuck';
    if (img.status === 'processing') {
      const p = img.processing?.progress;
      return `preparing the display${p ? ` · ${Math.round(p * 100)}%` : ' (waiting)'}`;
    }
    if (img.status === 'canceled') return 'upload canceled';
    if (img.status === 'error' && img.uploadedAt) return `preparation failed${img.processing?.error ? `: ${img.processing.error}` : ''}`;
    return 'upload failed';
  };
  // Images affichées par lecture directe de l'OME-TIFF (envoyées avant le découpage automatique) : lentes.
  const untiled = visibleImages.filter(img => !img.tiles && !img.devUrl);

  // Ligne d'une carte (liste principale ou canaux de calcul) : poignée de glisser-déposer, rangement, carte.
  const renderRow = ({ id, ch }) => {
    const img = ch && visibleImages.find(i => i.id === id);
    const name = ch ? ch.name : computedName(id);
    // Renommage (contributeurs) : nom de l'image, partagé partout, ou du canal calculé ; vide : nom par défaut.
    const onRename = canWrite ? next => (img
      ? renameImage(projectId, sampleId, id, next || defaultImageLabel(img.modality))
      : renameComputedChannel(projectId, sampleId, id, next || null)
    ).catch(err => window.alert(`Rename failed: ${err.message}`)) : undefined;
    const place = dropAt?.id === id && dragId !== id ? (dropAt.after ? ' drop-after' : ' drop-before') : '';
    return (
      <div key={id} className={`viewer-sortable${dragId === id ? ' dragging' : ''}${place}`}
        onDragOver={e => dragOver(e, id)} onDrop={drop}>
        {!presentation && (
          <>
            <span className="viewer-drag-handle" draggable role="button" tabIndex={0} data-drag={id}
              title="Drag to reorder (or up / down arrows)" aria-label={`Move ${name}`}
              onDragStart={e => startDrag(e, id)} onDragEnd={endDrag}
              onKeyDown={e => {
                if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                e.preventDefault();
                stepModality(id, e.key === 'ArrowUp' ? -1 : 1);
              }}>
              <DragIndicator fontSize="inherit" />
            </span>
            <button type="button" className="viewer-stash-toggle" onClick={() => setSection(id, !stash.includes(id))}
              title={stash.includes(id) ? 'Move back to the displayed modalities' : 'Move to the computation channels'}
              aria-label={stash.includes(id) ? `Move ${name} back to the modalities` : `Move ${name} to the computation channels`}>
              {stash.includes(id) ? <Visibility fontSize="inherit" /> : <VisibilityOff fontSize="inherit" />}
            </button>
          </>
        )}
        {ch ? (
          <ChannelCard {...fold(id)}
            channel={ch.rgb ? { ...ch, alwaysOn: true, visible: true } : { ...ch, visible: active === id }}
            onChange={patch => update(id, patch)} locked={presentation}
            onDelete={img && canRemove(img) ? () => removeImage(img) : undefined} onRename={onRename} />
        ) : id === COLLAGEN_ID ? (
          <CollagenCard {...fold(id)}
            label={labels[id]} onRename={onRename} channels={fluo} c={chCol}
            settings={collagen} stats={colStats} cell={colReady?.cell} otsuThreshold={colReady?.otsu}
            computing={collagenShown && !colField} active={active === COLLAGEN_ID}
            onToggle={on => { setActive(on ? COLLAGEN_ID : null); setSaveState(null); }}
            onChange={patch => {
              // autre SHG : seuil d'Otsu de nouveau
              setCollagen(prev => ({ ...prev, ...patch, ...('c' in patch ? { threshold: null } : {}) }));
              setSaveState(null);
            }} />
        ) : id === ATTENTION_ID ? (
          <AttentionCard {...fold(id)}
            label={labels[id]} onRename={onRename} maps={overlays} map={attMap} settings={attention} signed={attSigned}
            valueName={attValue} scale={attentionShown ? attFieldValue?.scale ?? null : null} range={attRange}
            bounds={attentionShown && attFieldValue?.signed ? [attFieldValue.low, attFieldValue.high] : null}
            zero={attView.zero}
            ignored={attIgnored}
            coverage={attentionShown ? attFieldValue?.coverage ?? null : null} busy={attBusy} active={active === ATTENTION_ID}
            canWrite={canWrite && !presentation} canDelete={map => profile?.role === 'admin' || map.createdBy === profile?.uid}
            onToggle={on => { setActive(on ? ATTENTION_ID : null); setSaveState(null); }}
            onChange={patch => { setAttention(prev => ({ ...prev, ...patch })); setSaveState(null); }}
            onImport={importAttention} onRefit={refitAttention} onDelete={removeAttention} />
        ) : id === VHE_ID ? (
          <VirtualHeCard {...fold(id)}
            label={labels[id]} onRename={onRename} channels={fluo} n={chN} r={chNR}
            e={chE} c={chC} settings={vhe} error={vheStack?.error} active={active === VHE_ID}
            onToggle={on => { setActive(on ? VHE_ID : null); setSaveState(null); }}
            onChange={patch => {
              // canaux : k, échelle et fenêtres à réestimer
              const reset = ['n', 'r', 'e', 'c'].some(key => key in patch)
                ? { k: null, hScale: null, eLow: null, eHigh: null, cLow: null, cHigh: null } : {};
              setVhe(prev => ({ ...prev, ...patch, ...reset }));
              setSaveState(null);
            }}
            onWindow={(chId, sourceWindow) => {
              update(chId, { sourceWindow });
              setVhe(prev => ({ ...prev, k: null, hScale: null }));
            }}
            onEstimate={estimateVheNow} />
        ) : id === NUCLEI_ID ? (
          <VirtualHeCard nucleiOnly {...fold(id)}
            label={labels[id]} onRename={onRename} channels={fluo} n={chH} r={chHR}
            settings={nuclei} error={nucStack?.error} active={active === NUCLEI_ID}
            onToggle={on => { setActive(on ? NUCLEI_ID : null); setSaveState(null); }}
            onChange={patch => {
              // canaux : k et échelle à réestimer
              const reset = ['n', 'r'].some(key => key in patch) ? { k: null, hScale: null } : {};
              setNuclei(prev => ({ ...prev, ...patch, ...reset }));
              setSaveState(null);
            }}
            onWindow={(chId, sourceWindow) => {
              update(chId, { sourceWindow });
              setNuclei(prev => ({ ...prev, k: null, hScale: null }));
            }}
            onEstimate={estimateNucleiNow} />
        ) : id === REDOX_ID ? (
          <RedoxCard {...fold(id)}
            label={labels[id]} onRename={onRename} channels={fluo} n={chRN} f={chRF}
            settings={redox} resolved={redoxResolved} stats={redoxStatsValue} error={redoxStack?.error}
            active={active === REDOX_ID}
            onToggle={on => { setActive(on ? REDOX_ID : null); setSaveState(null); }}
            onChange={patch => {
              // autre canal : son fond et ce qui en dépend à réestimer ; autre formule : la plage
              const reset = {
                ...('n' in patch ? { bgN: null } : {}), ...('f' in patch ? { bgF: null } : {}),
                ...('n' in patch || 'f' in patch ? { threshold: null, bright: null } : {}),
                ...('n' in patch || 'f' in patch || 'formula' in patch ? { lo: null, hi: null } : {}),
              };
              setRedox(prev => ({ ...prev, ...patch, ...reset }));
              setSaveState(null);
            }}
            onWindow={(chId, sourceWindow) => update(chId, { sourceWindow })} />
        ) : id === COMPOSITE_ID ? (
          <CompositeCard {...fold(id)}
            label={labels[id]} onRename={onRename} channels={fluo} chans={compChans} settings={composite} stats={compStatsReady}
            blacks={compBlacks}
            error={compositeStack?.error} active={active === COMPOSITE_ID}
            onToggle={on => { setActive(on ? COMPOSITE_ID : null); setSaveState(null); }}
            onChange={patch => { setComposite(prev => ({ ...prev, ...patch })); setSaveState(null); }}
            onBalance={async () => {
              const [gainR, gainG, gainB] = await balanceGains(compChans);
              setComposite(prev => ({ ...prev, gainR, gainG, gainB }));
              setSaveState(null);
            }}
            onRecipe={() => {
              const recipe = compositeRecipe({
                sample: sample?.name || sampleId, chans: compChans, windows: compWindows, settings: composite,
                stats: compStatsReady, transform: compTransform,
              });
              const url = URL.createObjectURL(new Blob([JSON.stringify(recipe, null, 1)], { type: 'application/json' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `composite_recipe_${(sample?.name || sampleId).replace(/[^\w.-]+/g, '_')}.json`;
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            }} />
        ) : id === BLOOD_ID ? (
          <BloodChannelCard {...fold(id)}
            label={labels[id]} onRename={onRename} channels={fluo} s={chS} r={chR}
            settings={blood} error={bloodPair?.error}
            glass={Object.fromEntries([chS, chR].filter(Boolean).map(ch => [ch.id,
              glass[ch.id] == null ? null : ch.toOriginal ? ch.toOriginal(glass[ch.id]) : glass[ch.id]]))}
            active={active === BLOOD_ID}
            onToggle={on => { setActive(on ? BLOOD_ID : null); setSaveState(null); }}
            onChange={patch => {
              // canaux, noir, affichage, mailles : k et plage à réestimer
              const reset = ['s', 'r', 'dark', 'display', 'heatLevel'].some(key => key in patch)
                ? { k: null, odMin: null, odMax: null } : {};
              setBlood(prev => ({ ...prev, ...patch, ...reset }));
              setSaveState(null);
            }}
            onWindow={(chId, sourceWindow) => {
              update(chId, { sourceWindow });
              setBlood(prev => ({ ...prev, k: null, odMin: null, odMax: null }));
            }}
            onEstimate={estimateBloodNow} />
        ) : (
          <DerivedChannelCard {...fold(id)}
            label={labels[id]} onRename={onRename} channels={fluo} a={chA} b={chB} settings={compare} error={pair?.error}
            active={active === DERIVED_ID}
            onToggle={on => { setActive(on ? DERIVED_ID : null); setSaveState(null); }}
            onChange={patch => { setCompare(prev => ({ ...prev, ...patch })); setSaveState(null); }}
            onAutoBalance={async () => {
              const balance = await autoBalance(chA.loader, chB.loader, chA.contrast, chB.contrast, derivedSettings);
              if (balance != null) { setCompare(prev => ({ ...prev, balance })); setSaveState(null); }
            }} />
        )}
      </div>
    );
  };

  return (
    <div id="viewer-page">
      <div id="viewer-canvas" ref={containerRef}>
        {!status && !first && <div className="viewer-loader" />}
        {/* Rideau : la vue du dessus (modalité active) n'est visible qu'à droite de la poignée. */}
        <div ref={curtainRef} className="viewer-curtain"
          style={{ display: curtainActive ? 'block' : 'none', clipPath: `inset(0 0 0 ${curtainPos * 100}%)` }} />
        {curtainActive && (
          <>
            <div className="viewer-curtain-handle" style={{ left: `${curtainPos * 100}%` }}
              onPointerDown={dragCurtain} title="Drag to compare">
              <span />
            </div>
            <span className="viewer-curtain-tag left">{he[0].name}</span>
            <span className="viewer-curtain-tag right">{activeName}</span>
          </>
        )}
        {/* Comparaison avec le HE par le rideau : interrupteur en haut à gauche. */}
        {canCompare && (
          <label className={`viewer-compare-toggle${compareHe ? ' on' : ''}`}
            title={`Compare "${activeName}" with HE using a curtain`}>
            <Compare fontSize="inherit" />
            Compare with HE
            <span className="switch">
              <input type="checkbox" checked={compareHe}
                onChange={e => { if (e.target.checked) centerCurtain(); setCompareHe(e.target.checked); setSaveState(null); }} />
              <span />
            </span>
          </label>
        )}
        {first && cropEdit && (
          <CropOverlay rect={crop || { x0: 0, y0: 0, x1: W, y1: H }} width={W} height={H} containerRef={containerRef}
            getView={() => viewStateRef.current} subscribe={subscribeView}
            onChange={rect => { setCrop(rect); setSaveState(null); }} />
        )}
        {first && !presentation && (
          <div className="viewer-tools">
            <button type="button" className={`viewer-tool${cropEdit ? ' on' : ''}`}
              onClick={() => { setCropEdit(on => !on); setEraseEdit(false); }}
              title="Crop: drag the edges or corners of the image to hide artifacts">
              <Crop fontSize="inherit" /> {cropEdit ? 'Done' : 'Crop'}
            </button>
            {cropEdit && crop && (
              <button type="button" className="viewer-tool" onClick={() => { setCrop(null); setSaveState(null); }}>
                Show all
              </button>
            )}
            {cropEdit && <span className="viewer-tool-hint">Drag the edges or corners of the image</span>}
            {!cropEdit && (
              <button type="button" className={`viewer-tool${eraseEdit ? ' on' : ''}`}
                onClick={() => { setEraseEdit(on => !on); setEraseNote(null); }}
                title="Eraser: erase dust and objects around the sample">
                <CleaningServices fontSize="inherit" /> {eraseEdit ? 'Done' : 'Erase'}
              </button>
            )}
            {!cropEdit && !eraseEdit && (
              <button type="button" className="viewer-tool" onClick={() => setExportOpen(true)}
                title="Download what is displayed at full resolution (pyramidal OME-TIFF)">
                <FileDownload fontSize="inherit" /> Download
              </button>
            )}
            {eraseEdit && (
              <>
                <button type="button" className="viewer-tool" onClick={detectErase} disabled={detecting || !canDetect}
                  title={canDetect
                    ? 'Erases isolated objects outside the tissue of the active modality; replaces its previous detection'
                    : 'Detection runs on fluorescence images: turn on a fluorescence image or the computed channel (on HE, circle by hand)'}>
                  {detecting ? 'Detecting…' : 'Detect dust'}
                </button>
                <button type="button" className="viewer-tool" disabled={!targetZones.length}
                  onClick={() => {
                    const last = targetZones[targetZones.length - 1];
                    setErase(list => list.filter(z => z !== last));
                    setSaveState(null);
                  }}>Undo</button>
                <button type="button" className="viewer-tool" disabled={!targetZones.length}
                  onClick={() => {
                    setErase(list => list.filter(z => !targetZones.includes(z)));
                    setEraseNote(null);
                    setSaveState(null);
                  }}>Restore all</button>
                <span className="viewer-tool-hint">
                  Erasing on "{targetName || 'no modality'}"
                  {targetClass === 'he' ? ' (areas hidden on the HES and the HES + map channel only)'
                    : targetClass ? ` (areas shared by the modalities on a ${targetClass === 'light' ? 'light' : 'dark'} background)` : ''} ·{' '}
                  {eraseNote ? `${eraseNote} · ` : ''}
                  {targetZones.length} area{targetZones.length > 1 ? 's' : ''} erased ·
                  circle an object to erase it, click an area to restore it · Shift + drag: pan
                </span>
              </>
            )}
          </div>
        )}
        {/* Barre de couleurs (en haut à droite, sous l'étiquette du rideau) et barre d'échelle (en bas à droite). */}
        {first && legend && (
          <div className={`viewer-legend top${curtainActive ? ' below-tag' : ''}`}><ColorBar legend={legend} /></div>
        )}
        {first && (
          <div className="viewer-legend bottom">
            <ScaleBar umPerPx={umPerPx} source={umSource} getView={() => viewStateRef.current} subscribe={subscribeView}
              onSet={canWrite ? setPixel : undefined} />
          </div>
        )}
      </div>
      <aside id="viewer-sidebar">
        <div className="viewer-sidebar-header">
          <div className="viewer-header-top">
            <img className="viewer-logo" src={LOGO} alt="HyprView" />
            <ExternalPreviewButton />
          </div>
          {/* Démo ouverte par un externe : projet illisible, retour à l'accueil. */}
          <Link className="viewer-back" to={projectShown ? `${ROUTES.FILE_BROWSER}/${projectId}` : ROUTES.HOME}>
            ← {projectShown ? projectShown.name : 'Home'}
          </Link>
          <p className="viewer-sample-name">
            {sample?.name || ''}
            {presentation && demo && <span className="viewer-demo-badge">Demo · read-only</span>}
          </p>
          <p className={`viewer-sample-meta${status ? ' error' : ''}`}>
            {status || (first
              ? `${W.toLocaleString('en-US')} × ${H.toLocaleString('en-US')} px · ${first.loader.length} levels · ${first.loader[0].dtype}`
              : 'Loading…')}
          </p>
          {previewClosed && (
            <p className="viewer-preview-note">
              Preview: an external account can't open this sample yet (it is neither a demo nor in a project shared with it).
            </p>
          )}
          {/* Administrateurs, vue externe comprise : l'aperçu montre aussitôt l'effet. */}
          {(profile?.role === 'admin' || profile?.realRole === 'admin') && sample && (
            <label className="viewer-demo-toggle"
              title="Demo: every active account, external ones included, opens it read-only (displayed modalities and their toggles only)">
              <span className="switch">
                <input type="checkbox" checked={!!demo} onChange={e => toggleDemo(e.target.checked)} />
                <span />
              </span>
              Demo for all accounts (read-only)
            </label>
          )}
        </div>
        <div className={`viewer-section-title${dropSection === 'main' ? ' drop' : ''}`}
          onDragOver={e => sectionDragOver(e, 'main')} onDragLeave={() => setDropSection(null)} onDrop={e => sectionDrop(e, false)}>
          Modalities
          {cards.length > 0 && !presentation && (
            <button type="button" className="viewer-section-action"
              onClick={() => setExpanded(cards.some(c => expanded[c.id]) ? {} : Object.fromEntries(cards.map(c => [c.id, true])))}>
              {cards.some(c => expanded[c.id]) ? 'Collapse all' : 'Expand all'}
            </button>
          )}
        </div>
        <div className="viewer-channels">
          {/* HE toujours affiché (sans interrupteur) ; fluorescences et canal calculé : une seule active. Poignée à
              gauche de chaque carte : glisser pour réorganiser. */}
          {shownCards.map(renderRow)}
          {/* Canaux de calcul : cartes rangées à part (repliées par défaut), qui ne servent qu'aux canaux calculés ;
              cachés en présentation. */}
          {!presentation && (
            <>
              <div className={`viewer-stash-title${dropSection === 'stash' ? ' drop' : ''}`} role="button" tabIndex={0}
                onClick={() => setStashOpen(o => !o)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStashOpen(o => !o); } }}
                onDragOver={e => sectionDragOver(e, 'stash')} onDragLeave={() => setDropSection(null)} onDrop={e => sectionDrop(e, true)}
                title="Channels only used to compute other channels: drag a card here to move it">
                <ExpandMore className={`channel-chevron${stashOpen ? '' : ' collapsed'}`} fontSize="inherit" />
                Computation channels ({stashCards.length})
              </div>
              {stashOpen && stashCards.map(renderRow)}
              {stashOpen && !stashCards.length && (
                <p className="viewer-note">Drag here the cards of channels only used for computing (or use the icon under their handle).</p>
              )}
            </>
          )}
          {pending > 0 && <p className="viewer-note">Loading {pending} modalit{pending > 1 ? 'ies' : 'y'}…</p>}
          {failures.map(img => (
            <p key={img.id} className="viewer-note error">{img.label || img.modality}: {loaded[img.id].error}</p>
          ))}
          {!presentation && unfinished.map(img => (
            <p key={img.id} className="viewer-note viewer-unfinished">
              <span>{img.label || img.modality} · {unfinishedLabel(img)}</span>
              <span className="viewer-unfinished-actions">
                {canWrite && img.status === 'error' && img.uploadedAt && (
                  <button type="button" onClick={() => prepare(img)}>Retry</button>
                )}
                {canWrite && stalled(img) && <button type="button" onClick={() => relaunch(img)}>Restart</button>}
                {canRemove(img) && <button type="button" onClick={() => removeUnfinished(img)}>Remove</button>}
              </span>
            </p>
          ))}
          {canWrite && untiled.map(img => (
            <p key={img.id} className="viewer-note viewer-unfinished">
              <span>{img.label || img.modality} · slow display (image not split into tiles)</span>
              <span className="viewer-unfinished-actions">
                <button type="button" onClick={() => prepare(img)}>Prepare</button>
              </span>
            </p>
          ))}
          {canWrite && (
            <div className="viewer-save">
              <button type="button" className="viewer-button" onClick={() => setAddOpen(true)}>+ Add images</button>
            </div>
          )}
          {canWrite && channels.length > 0 && (
            <div className="viewer-save">
              <button type="button" className="viewer-button" onClick={saveDefaults} disabled={saveState === 'saving'}>
                Save as default settings
              </button>
              {saveState === 'saved' && <span className="viewer-note">Saved for all users.</span>}
              {saveState?.startsWith?.('Failed') && <span className="viewer-note error">{saveState}</span>}
            </div>
          )}
        </div>
        <div className="viewer-watermark"><img src={WATERMARK} alt="" /></div>
      </aside>
      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} getSnapshot={() => exportRef.current}
        region={exportRegion} name={exportName} pixelSizeUm={umPerPx}
        fileName={`${`${sample?.name || 'sample'} - ${exportName}`.replace(/[\\/:*?"<>|]+/g, '_')}.ome.tif`} />
      <AddImageDialog open={addOpen} onClose={() => setAddOpen(false)} profile={profile}
        projectId={projectId} sampleId={sampleId} existing={images || []} />
    </div>
  );
}

export default withAuthorization(authUser => !!authUser)(ViewerPage);
